import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getContacts, getGroups } from '../../api-wa/contactsApi';
import { getTemplates } from '../../api-wa/templatesApi';
import { 
  getCampaign, 
  getCampaigns, 
  getExcludedContacts, 
  createDraftCampaign, 
  launchCampaignNew 
} from '../../api-wa/campaignsApi';
import { apiClient, getApiErrorMessage } from '../../api-wa';
import { ToastContext } from '../../Context/WaToastContext';

const REPLY_CATEGORIES = [
  { value: 'INTERESTED', label: '🟢 Interested / Attending' },
  { value: 'LATER_DATES', label: '📅 Interested for Later Dates' },
  { value: 'NOT_INTERESTED', label: '🔴 Not Interested' },
  { value: 'NO_RESPONSE', label: '⏳ No Response Yet' },
];

const BUTTON_ACTION_OPTIONS = [
  { value: 'CONTINUE', label: '🟢 Continue Reminder Sequence' },
  { value: 'STOP', label: '🔴 Stop Reminder Sequence' },
  { value: 'SEND_TEMPLATE', label: '📨 Send Follow-up Template' },
];

const ANALYTICS_CATEGORIES = [
  { value: 'INTERESTED', label: '🟢 Interested / Attending' },
  { value: 'LATER_DATES', label: '📅 Interested for Later Dates' },
  { value: 'NOT_INTERESTED', label: '🔴 Not Interested' },
  { value: 'OTHER', label: '⚪ Other / Unmapped' },
];

// Helper to extract text preview and variables
const extractTemplateBodyText = (tpl) => {
  if (!tpl) return '';
  if (tpl.body) return tpl.body;
  if (Array.isArray(tpl.components)) {
    const bodyComp = tpl.components.find(c => c.type === 'BODY');
    if (bodyComp?.text) return bodyComp.text;
  }
  return '';
};

const extractTemplateVariables = (text) => {
  if (!text) return [];
  const matches = text.match(/\{\{([^}]+)\}\}/g) || [];
  return Array.from(new Set(matches.map(m => m.replace(/[\{\}]/g, '').trim()))).filter(Boolean);
};

const extractTemplateButtons = (tpl) => {
  if (!tpl) return [];
  if (Array.isArray(tpl.buttons)) {
    return tpl.buttons.map(b => (typeof b === 'object' ? b.text : b)).filter(Boolean);
  }
  if (Array.isArray(tpl.components)) {
    const btnComp = tpl.components.find(c => c.type === 'BUTTONS');
    if (btnComp && Array.isArray(btnComp.buttons)) {
      return btnComp.buttons.map(b => (typeof b === 'object' ? b.text : b)).filter(Boolean);
    }
  }
  return [];
};

const addMinutesToDateTimeLocal = (dtString, minutesToAdd) => {
  const baseDate = dtString ? new Date(dtString) : new Date();
  if (isNaN(baseDate.getTime())) return '';
  const newDate = new Date(baseDate.getTime() + minutesToAdd * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = newDate.getFullYear();
  const mm = pad(newDate.getMonth() + 1);
  const dd = pad(newDate.getDate());
  const hh = pad(newDate.getHours());
  const min = pad(newDate.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const formatDateTimeDisplay = (dtString) => {
  if (!dtString) return 'Unscheduled';
  try {
    const d = new Date(dtString);
    if (isNaN(d.getTime())) return dtString;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    return dtString;
  }
};

const CreateCampaign = ({ onBack, onSaved, onCreateTemplate, setIsDirty }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useContext(ToastContext) || { 
    showError: (m) => alert(m), 
    showSuccess: (m) => alert(m) 
  };

  // Step state (1: Setup, 2: Messages, 3: Review)
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isFinishing, setIsFinishing] = useState(false);

  // Card 1: Campaign details
  const [campaignName, setCampaignName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [editCampaignDraftId, setEditCampaignDraftId] = useState(null);

  // Card 2: Audience state
  const [audienceTab, setAudienceTab] = useState('manual'); // 'manual' | 'groups' | 'replies'
  const [allContacts, setAllContacts] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');

  // Audience from Groups tab
  const [sourceGroupId, setSourceGroupId] = useState('');

  // Audience from Campaign Replies tab
  const [sourceCampaignId, setSourceCampaignId] = useState('');
  const [sourceCategory, setSourceCategory] = useState('INTERESTED');
  const [sourceReplyContacts, setSourceReplyContacts] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);

  // Card 3: Exclude people state
  const [pastCampaigns, setPastCampaigns] = useState([]);
  const [excludeAlreadySent, setExcludeAlreadySent] = useState(false);
  const [excludeAllCampaigns, setExcludeAllCampaigns] = useState(true);
  const [selectedExcludeCampaignIds, setSelectedExcludeCampaignIds] = useState([]);
  const [campaignExcludedContactIds, setCampaignExcludedContactIds] = useState(new Set());
  const [campaignExcludedPhoneNumbers, setCampaignExcludedPhoneNumbers] = useState(new Set());
  const [isCheckingCampaignExclusions, setIsCheckingCampaignExclusions] = useState(false);

  // Group exclusions
  const [excludedGroupIds, setExcludedGroupIds] = useState(new Set());

  // Individual contact exclusions & Re-include overrides
  const [individualExcludedContactIds, setIndividualExcludedContactIds] = useState(new Set());
  const [overrideAllowedContactIds, setOverrideAllowedContactIds] = useState(new Set());
  const [exclusionSearchQuery, setExclusionSearchQuery] = useState('');

  // Validation & Toast state
  const [fieldErrors, setFieldErrors] = useState({});
  const [localToast, setLocalToast] = useState(null);

  const showNotification = useCallback((message, type = 'error') => {
    setLocalToast({ message, type });
    if (type === 'error' && showError) showError(message);
    if (type === 'success' && showSuccess) showSuccess(message);
    setTimeout(() => {
      setLocalToast(prev => (prev?.message === message ? null : prev));
    }, 4500);
  }, [showError, showSuccess]);

  // Step 2 (Messages & Reminders) state
  const [timeZone, setTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata');
  const [templates, setTemplates] = useState([]);
  const [activeSelection, setActiveSelection] = useState({ type: 'MESSAGE', messageId: 1, reminderId: null });
  const [messages, setMessages] = useState([
    {
      id: 1,
      title: 'Message 1',
      timingType: 'SCHEDULED', // 'SCHEDULED' | 'IMMEDIATE'
      scheduledAt: '',
      templateId: '',
      headerVariable: '',
      bodyVariables: {},
      buttonActions: {},
      reminders: []
    }
  ]);

  // Navigate back helper
  const handleGoBack = () => {
    if (onBack) onBack();
    else navigate('/whatsapp/campaigns');
  };

  // Fetch initial data
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [contactsData, groupsData, pastCamps, templatesData] = await Promise.all([
          getContacts({ page_size: 10000 }).catch(() => []),
          getGroups().catch(() => []),
          getCampaigns().catch(() => []),
          getTemplates().catch(() => [])
        ]);

        if (!isMounted) return;

        const loadedContacts = Array.isArray(contactsData?.results) 
          ? contactsData.results 
          : (Array.isArray(contactsData) ? contactsData : []);
        setAllContacts(loadedContacts);

        const loadedGroups = Array.isArray(groupsData?.results)
          ? groupsData.results
          : (Array.isArray(groupsData) ? groupsData : []);
        setContactGroups(loadedGroups);

        const loadedCampaigns = Array.isArray(pastCamps?.results)
          ? pastCamps.results
          : (Array.isArray(pastCamps) ? pastCamps : []);
        setPastCampaigns(loadedCampaigns);

        const loadedTemplates = Array.isArray(templatesData?.results)
          ? templatesData.results
          : (Array.isArray(templatesData) ? templatesData : []);
        setTemplates(loadedTemplates);

        // Check for draft editing
        const draftId = localStorage.getItem('editCampaignDraftId');
        if (draftId) {
          setEditCampaignDraftId(draftId);
          try {
            const draft = await getCampaign(draftId);
            if (draft && isMounted) {
              setCampaignName(draft.name || '');
              setDescription(draft.description || '');
              if (draft.is_active !== undefined) setIsActive(Boolean(draft.is_active));
              if (draft.time_zone) setTimeZone(draft.time_zone);
              
              if (Array.isArray(draft.contacts)) {
                const draftPhones = new Set(draft.contacts.map(p => String(p).replace(/\D/g, '')));
                const matchedIds = loadedContacts
                  .filter(c => {
                    const phone = String(c.phone_number || c.phone || '').replace(/\D/g, '');
                    return draftPhones.has(phone) || (phone.length >= 10 && draftPhones.has(phone.slice(-10)));
                  })
                  .map(c => c.id);
                setSelectedContactIds(matchedIds);
              }

              // Load existing message schedules if present
              if (Array.isArray(draft.schedules) && draft.schedules.length > 0) {
                const primarySchedules = draft.schedules.filter(s => !s.parent_schedule && s.timing_type !== 'RELATIVE');
                const subReminders = draft.schedules.filter(s => s.parent_schedule || s.timing_type === 'RELATIVE');

                const loadedMessages = (primarySchedules.length > 0 ? primarySchedules : draft.schedules).map((s, idx) => {
                  const childReminders = subReminders.filter(sub => {
                    const pId = typeof sub.parent_schedule === 'object' ? sub.parent_schedule?.id : sub.parent_schedule;
                    return pId && (pId === s.id || String(pId) === String(s.id));
                  }).map((sub, rIdx) => {
                    const mins = sub.relative_delay_minutes || 60;
                    let dVal = mins;
                    let dUnit = 'minutes';
                    if (mins % 1440 === 0) {
                      dVal = mins / 1440;
                      dUnit = 'days';
                    } else if (mins % 60 === 0) {
                      dVal = mins / 60;
                      dUnit = 'hours';
                    }

                    return {
                      id: sub.id || `rem_${idx}_${rIdx}`,
                      delayValue: dVal,
                      delayUnit: dUnit,
                      skipIfReplied: sub.skip_if_replied !== false,
                      templateId: sub.template_id || sub.template || '',
                      headerVariable: sub.header_variable || '',
                      bodyVariables: sub.variables || {},
                      buttonActions: sub.button_actions || {}
                    };
                  });

                  return {
                    id: s.id || idx + 1,
                    title: s.title || `Message ${idx + 1}`,
                    timingType: s.timing_type || 'SCHEDULED',
                    scheduledAt: s.scheduled_at ? s.scheduled_at.slice(0, 16) : '',
                    templateId: s.template_id || s.template || '',
                    headerVariable: s.header_variable || '',
                    bodyVariables: s.variables || {},
                    buttonActions: s.button_actions || {},
                    reminders: childReminders
                  };
                });

                setMessages(loadedMessages);
                if (loadedMessages[0]?.id) {
                  setActiveSelection({ type: 'MESSAGE', messageId: loadedMessages[0].id, reminderId: null });
                }
              } else if (draft.template) {
                const tId = typeof draft.template === 'object' ? draft.template.id : draft.template;
                setMessages([{
                  id: 1,
                  title: 'Message 1',
                  timingType: 'SCHEDULED',
                  scheduledAt: draft.scheduled_at ? draft.scheduled_at.slice(0, 16) : '',
                  templateId: tId || '',
                  headerVariable: draft.variables?.__header_media_url__ || '',
                  bodyVariables: draft.variables || {},
                  buttonActions: {},
                  reminders: []
                }]);
                setActiveSelection({ type: 'MESSAGE', messageId: 1, reminderId: null });
              }
            }
          } catch (e) {
            console.error('Failed to load draft:', e);
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, []);

  // Fetch campaign exclusions whenever excludeAlreadySent, excludeAllCampaigns, or selectedExcludeCampaignIds change
  useEffect(() => {
    if (!excludeAlreadySent) {
      setCampaignExcludedContactIds(new Set());
      setCampaignExcludedPhoneNumbers(new Set());
      return;
    }

    if (!excludeAllCampaigns && selectedExcludeCampaignIds.length === 0) {
      setCampaignExcludedContactIds(new Set());
      setCampaignExcludedPhoneNumbers(new Set());
      return;
    }

    let isMounted = true;
    const fetchExclusions = async () => {
      try {
        setIsCheckingCampaignExclusions(true);
        const res = await getExcludedContacts({
          excludeAll: excludeAllCampaigns,
          campaignIds: excludeAllCampaigns ? [] : selectedExcludeCampaignIds
        });

        if (!isMounted || !res) return;

        const cids = new Set();
        (res.excluded_contact_ids || []).forEach(x => {
          cids.add(x);
          cids.add(Number(x));
          cids.add(String(x));
        });

        const phones = new Set();
        (res.excluded_phone_numbers || []).forEach(p => {
          const s = String(p).replace(/\D/g, '');
          phones.add(s);
          if (s.length >= 10) phones.add(s.slice(-10));
        });

        setCampaignExcludedContactIds(cids);
        setCampaignExcludedPhoneNumbers(phones);
      } catch (err) {
        console.error('Failed to fetch excluded contacts:', err);
      } finally {
        if (isMounted) setIsCheckingCampaignExclusions(false);
      }
    };

    fetchExclusions();
    return () => { isMounted = false; };
  }, [excludeAlreadySent, excludeAllCampaigns, selectedExcludeCampaignIds]);

  // Load contacts for "From campaign replies"
  useEffect(() => {
    if (audienceTab !== 'replies' || !sourceCampaignId) return;

    let isMounted = true;
    const fetchReplyContacts = async () => {
      try {
        setLoadingReplies(true);
        const res = await apiClient.get('/reminders/analytics/', { params: { campaign_id: sourceCampaignId } });
        if (!isMounted) return;
        const contactsList = res.data?.contacts || [];
        const filtered = contactsList.filter(row => row.category === sourceCategory);
        setSourceReplyContacts(filtered);
      } catch (err) {
        console.error('Failed to load campaign replies contacts:', err);
        if (isMounted) setSourceReplyContacts([]);
      } finally {
        if (isMounted) setLoadingReplies(false);
      }
    };

    fetchReplyContacts();
    return () => { isMounted = false; };
  }, [audienceTab, sourceCampaignId, sourceCategory]);

  // Check if contact belongs to an excluded group
  const isContactInAnyExcludedGroup = useCallback((contact) => {
    if (excludedGroupIds.size === 0 || !contact) return false;
    if (Array.isArray(contact.groups)) {
      return contact.groups.some(g => {
        const gid = typeof g === 'object' ? g?.id : g;
        return excludedGroupIds.has(gid) || excludedGroupIds.has(Number(gid)) || excludedGroupIds.has(String(gid));
      });
    }
    if (contact.group) {
      const gid = typeof contact.group === 'object' ? contact.group?.id : contact.group;
      return excludedGroupIds.has(gid) || excludedGroupIds.has(Number(gid)) || excludedGroupIds.has(String(gid));
    }
    return false;
  }, [excludedGroupIds]);

  // 1. Check if contact is excluded by campaign (Already Sent)
  const isContactExcludedByCampaign = useCallback((contact) => {
    if (!excludeAlreadySent || !contact) return false;
    const cid = contact.id ?? contact.contact_id;
    if (cid && (overrideAllowedContactIds.has(cid) || overrideAllowedContactIds.has(Number(cid)) || overrideAllowedContactIds.has(String(cid)))) {
      return false; // Explicitly re-included by user!
    }
    if (cid && (campaignExcludedContactIds.has(cid) || campaignExcludedContactIds.has(Number(cid)) || campaignExcludedContactIds.has(String(cid)))) {
      return true;
    }
    const rawPhone = String(contact.phone_number || contact.phone || '').replace(/\D/g, '');
    if (rawPhone && campaignExcludedPhoneNumbers.has(rawPhone)) return true;
    if (rawPhone && rawPhone.length >= 10 && campaignExcludedPhoneNumbers.has(rawPhone.slice(-10))) return true;
    return false;
  }, [excludeAlreadySent, campaignExcludedContactIds, campaignExcludedPhoneNumbers, overrideAllowedContactIds]);

  // 2. Check if contact is excluded individually
  const isContactExcludedIndividually = useCallback((contact) => {
    if (!contact) return false;
    const cid = contact.id ?? contact.contact_id;
    return Boolean(cid && (individualExcludedContactIds.has(cid) || individualExcludedContactIds.has(String(cid)) || individualExcludedContactIds.has(Number(cid))));
  }, [individualExcludedContactIds]);

  // Check if contact is excluded by any of the 3 rules
  const isContactExcluded = useCallback((contact) => {
    if (!contact) return false;
    if (isContactExcludedIndividually(contact)) return true;
    if (isContactInAnyExcludedGroup(contact)) return true;
    if (isContactExcludedByCampaign(contact)) return true;
    return false;
  }, [isContactExcludedIndividually, isContactInAnyExcludedGroup, isContactExcludedByCampaign]);

  // Filtered contacts for Manual Selection
  const visibleContacts = useMemo(() => {
    return allContacts.filter(contact => {
      if (selectedGroupFilter !== 'all') {
        const hasGrp = Array.isArray(contact.groups) 
          ? contact.groups.some(g => (typeof g === 'object' ? g?.id === Number(selectedGroupFilter) : g === Number(selectedGroupFilter)))
          : (contact.group === Number(selectedGroupFilter) || contact.group?.id === Number(selectedGroupFilter));
        if (!hasGrp) return false;
      }
      if (contactSearchQuery.trim()) {
        const q = contactSearchQuery.toLowerCase().trim();
        const nameMatch = contact.name && contact.name.toLowerCase().includes(q);
        const phoneMatch = (contact.phone_number || contact.phone || '').includes(q);
        if (!nameMatch && !phoneMatch) return false;
      }
      return true;
    });
  }, [allContacts, selectedGroupFilter, contactSearchQuery]);

  // Contacts for Group tab
  const groupAudienceContacts = useMemo(() => {
    if (!sourceGroupId) return [];
    return allContacts.filter(c => {
      if (Array.isArray(c.groups)) {
        return c.groups.some(g => (typeof g === 'object' ? g?.id === Number(sourceGroupId) : g === Number(sourceGroupId)));
      }
      return c.group === Number(sourceGroupId) || c.group?.id === Number(sourceGroupId);
    });
  }, [allContacts, sourceGroupId]);

  // Exclusion search filter for individual contacts
  const visibleExclusionContacts = useMemo(() => {
    return allContacts.filter(contact => {
      if (exclusionSearchQuery.trim()) {
        const q = exclusionSearchQuery.toLowerCase().trim();
        const nameMatch = contact.name && contact.name.toLowerCase().includes(q);
        const phoneMatch = (contact.phone_number || contact.phone || '').includes(q);
        if (!nameMatch && !phoneMatch) return false;
      }
      return true;
    });
  }, [allContacts, exclusionSearchQuery]);

  // Live Formula Calculations
  const selectedCount = selectedContactIds.length;
  const effectiveExcludedCount = useMemo(() => {
    return selectedContactIds.filter(id => {
      const contact = allContacts.find(c => c.id === id || String(c.id) === String(id));
      return contact ? isContactExcluded(contact) : false;
    }).length;
  }, [selectedContactIds, allContacts, isContactExcluded]);

  const finalWillReceiveCount = Math.max(0, selectedCount - effectiveExcludedCount);

  // Audience Selection Handlers
  const toggleContactSelection = (id) => {
    const contact = allContacts.find(c => c.id === id || String(c.id) === String(id));
    if (contact && isContactInAnyExcludedGroup(contact)) {
      showNotification('This contact belongs to an excluded group and cannot be selected', 'error');
      return;
    }
    const isOriginallyByCampaign = contact && excludeAlreadySent && (
      (contact.id && (campaignExcludedContactIds.has(contact.id) || campaignExcludedContactIds.has(Number(contact.id)) || campaignExcludedContactIds.has(String(contact.id)))) ||
      (contact.phone && (campaignExcludedPhoneNumbers.has(String(contact.phone).replace(/\D/g, '')) || campaignExcludedPhoneNumbers.has(String(contact.phone).replace(/\D/g, '').slice(-10))))
    );
    if (isOriginallyByCampaign) {
      toggleOverrideAllowedContact(id);
      return;
    }
    if (contact && isContactExcludedIndividually(contact)) {
      showNotification('This contact was individually excluded in Card 3. Uncheck them in Card 3 to include.', 'error');
      return;
    }
    setSelectedContactIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (next.length > 0) setFieldErrors(pe => ({ ...pe, audience: null }));
      return next;
    });
  };

  const handleSelectAllShown = () => {
    const eligibleShownIds = visibleContacts.filter(c => !isContactExcluded(c)).map(c => c.id);
    const allSelected = eligibleShownIds.length > 0 && eligibleShownIds.every(id => selectedContactIds.includes(id));
    if (allSelected) {
      setSelectedContactIds(prev => prev.filter(id => !eligibleShownIds.includes(id)));
    } else {
      setSelectedContactIds(prev => Array.from(new Set([...prev, ...eligibleShownIds])));
      setFieldErrors(prev => ({ ...prev, audience: null }));
    }
  };

  const handleSelectGroupContacts = () => {
    const eligibleIds = groupAudienceContacts.filter(c => !isContactExcluded(c)).map(c => c.id);
    setSelectedContactIds(prev => Array.from(new Set([...prev, ...eligibleIds])));
    if (eligibleIds.length > 0) setFieldErrors(prev => ({ ...prev, audience: null }));
  };

  const handleDeselectGroupContacts = () => {
    const groupIds = new Set(groupAudienceContacts.map(c => c.id));
    setSelectedContactIds(prev => prev.filter(id => !groupIds.has(id)));
  };

  const handleSelectReplyContacts = () => {
    const matchedIds = sourceReplyContacts
      .map(row => {
        const match = allContacts.find(c => 
          c.id === row.contact_id || 
          String(c.phone_number || c.phone || '').endsWith(String(row.phone_number || '').slice(-10))
        );
        return match ? match.id : row.contact_id;
      })
      .filter(Boolean);
    setSelectedContactIds(prev => Array.from(new Set([...prev, ...matchedIds])));
    if (matchedIds.length > 0) setFieldErrors(prev => ({ ...prev, audience: null }));
  };

  // Exclusion Handlers
  const toggleExcludeCampaign = (campId) => {
    setSelectedExcludeCampaignIds(prev => 
      prev.includes(campId) ? prev.filter(x => x !== campId) : [...prev, campId]
    );
  };

  const toggleExcludeGroup = (groupId) => {
    setExcludedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const toggleOverrideAllowedContact = (contactId) => {
    const cid = Number(contactId);
    setOverrideAllowedContactIds(prev => {
      const next = new Set(prev);
      const isCurrentlyAllowed = next.has(cid) || next.has(String(contactId));
      if (isCurrentlyAllowed) {
        next.delete(cid);
        next.delete(String(contactId));
        setSelectedContactIds(s => s.filter(x => x !== cid && String(x) !== String(contactId)));
        showNotification('Contact returned to campaign exclusion (will not receive)', 'error');
      } else {
        next.add(cid);
        next.add(String(contactId));
        setSelectedContactIds(s => Array.from(new Set([...s, cid])));
        setFieldErrors(pe => ({ ...pe, audience: null }));
        showNotification('Contact re-included! Added to audience and will receive this campaign.', 'success');
      }
      return next;
    });
  };

  const toggleExcludeIndividualContact = (contactId) => {
    setIndividualExcludedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  const handleToggleSelectAllExclusion = () => {
    const targetIds = visibleExclusionContacts.map(c => c.id);
    const allSelected = targetIds.length > 0 && targetIds.every(id => individualExcludedContactIds.has(id));

    setIndividualExcludedContactIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        targetIds.forEach(id => next.delete(id));
      } else {
        targetIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Step 1 Validation & Proceed
  const handleSaveAndContinueStep1 = () => {
    const errors = {};
    if (!campaignName.trim()) {
      errors.name = 'Please enter a campaign name';
    }
    if (selectedContactIds.length === 0) {
      errors.audience = 'Please select at least one contact for your audience in Card 2';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstError = Object.values(errors)[0];
      showNotification(firstError, 'error');

      if (errors.name) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const nameInput = document.getElementById('campaign-name-input');
        if (nameInput) nameInput.focus();
      } else if (errors.audience) {
        const audCard = document.getElementById('audience-card-section');
        if (audCard) audCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (finalWillReceiveCount === 0) {
      showNotification(
        `All ${selectedContactIds.length} selected audience contacts are currently excluded. Please adjust your exclusions so at least 1 contact will receive.`,
        'error'
      );
      const exclCard = document.getElementById('exclude-card-section');
      if (exclCard) exclCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setFieldErrors({});
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Step 2 Active Message & Reminder Selection State ──
  const activeMessage = useMemo(() => {
    return messages.find(m => m.id === activeSelection.messageId) || messages[0] || null;
  }, [messages, activeSelection.messageId]);

  const activeMessageIndex = useMemo(() => {
    return messages.findIndex(m => m.id === activeMessage?.id);
  }, [messages, activeMessage]);

  const activeReminder = useMemo(() => {
    if (activeSelection.type !== 'REMINDER' || !activeMessage) return null;
    return (activeMessage.reminders || []).find(r => r.id === activeSelection.reminderId) || null;
  }, [activeSelection, activeMessage]);

  // Backward compatibility helper
  const setActiveMessageId = (id) => {
    setActiveSelection({ type: 'MESSAGE', messageId: id, reminderId: null });
  };

  const chronologicalTimeline = useMemo(() => {
    const list = [];
    messages.forEach((m, mIdx) => {
      list.push({
        id: m.id,
        messageId: m.id,
        reminderId: null,
        type: 'MESSAGE',
        title: m.title || `Message ${mIdx + 1}`,
        timingType: m.timingType,
        scheduledAt: m.scheduledAt,
        templateId: m.templateId,
        bodyVariables: m.bodyVariables,
        isReminder: false
      });
      (m.reminders || []).forEach(rem => {
        let delayMins = Number(rem.delayValue) || 60;
        if (rem.delayUnit === 'hours') delayMins = delayMins * 60;
        if (rem.delayUnit === 'days') delayMins = delayMins * 1440;

        let sendAt = '';
        if (m.scheduledAt) {
          sendAt = addMinutesToDateTimeLocal(m.scheduledAt, delayMins);
        }
        list.push({
          id: rem.id,
          messageId: m.id,
          reminderId: rem.id,
          type: 'REMINDER',
          title: `↳ ${m.title || `Message ${mIdx + 1}`} Reminder (+${rem.delayValue} ${rem.delayUnit})`,
          timingType: 'RELATIVE',
          scheduledAt: sendAt,
          delayMins,
          templateId: rem.templateId,
          bodyVariables: rem.bodyVariables,
          skipIfReplied: rem.skipIfReplied,
          isReminder: true
        });
      });
    });

    return list.sort((a, b) => {
      if (a.timingType === 'IMMEDIATE' && b.timingType !== 'IMMEDIATE') return -1;
      if (b.timingType === 'IMMEDIATE' && a.timingType !== 'IMMEDIATE') return 1;
      if (!a.scheduledAt) return 1;
      if (!b.scheduledAt) return -1;
      return new Date(a.scheduledAt) - new Date(b.scheduledAt);
    });
  }, [messages]);

  // ── Step 2 Multi-Message Handlers ──
  const handleAddNextDayMessage = () => {
    const lastMsgWithDate = [...messages].reverse().find(m => m.scheduledAt);
    let nextDateStr = '';
    if (lastMsgWithDate?.scheduledAt) {
      nextDateStr = addMinutesToDateTimeLocal(lastMsgWithDate.scheduledAt, 1440); // +24 hours
    } else {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(10, 0, 0, 0);
      const pad = n => String(n).padStart(2, '0');
      nextDateStr = `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}T10:00`;
    }

    const nextId = Date.now();
    const newMsg = {
      id: nextId,
      title: `Message ${messages.length + 1}`,
      timingType: 'SCHEDULED',
      scheduledAt: nextDateStr,
      templateId: '',
      headerVariable: '',
      bodyVariables: {},
      buttonActions: {},
      reminders: []
    };

    setMessages(prev => [...prev, newMsg]);
    setActiveSelection({ type: 'MESSAGE', messageId: nextId, reminderId: null });
    showNotification(`Added Message ${messages.length + 1} for next day!`, 'success');
  };

  const handleDuplicateMessage = (msgId) => {
    const targetIdx = messages.findIndex(m => m.id === msgId);
    if (targetIdx === -1) return;
    const target = messages[targetIdx];

    const nextId = Date.now();
    const duplicated = {
      ...JSON.parse(JSON.stringify(target)),
      id: nextId,
      title: `${target.title || 'Message'} (Copy)`,
      scheduledAt: target.scheduledAt ? addMinutesToDateTimeLocal(target.scheduledAt, 1440) : '',
      reminders: (target.reminders || []).map((r, rIdx) => ({
        ...JSON.parse(JSON.stringify(r)),
        id: `rem_${nextId}_${rIdx}`
      }))
    };

    setMessages(prev => {
      const updated = [...prev];
      updated.splice(targetIdx + 1, 0, duplicated);
      return updated;
    });
    setActiveSelection({ type: 'MESSAGE', messageId: nextId, reminderId: null });
    showNotification('Message duplicated successfully!', 'success');
  };

  const handleRemoveMessage = (msgId) => {
    if (messages.length <= 1) {
      showNotification('A campaign must have at least one message', 'error');
      return;
    }
    const idx = messages.findIndex(m => m.id === msgId);
    const newMessages = messages.filter(m => m.id !== msgId);
    setMessages(newMessages);

    if (activeSelection.messageId === msgId) {
      const fallbackIdx = Math.max(0, idx - 1);
      setActiveSelection({
        type: 'MESSAGE',
        messageId: newMessages[fallbackIdx]?.id || newMessages[0]?.id,
        reminderId: null
      });
    }
    showNotification('Message removed', 'info');
  };

  const handleUpdateActiveField = (field, value) => {
    if (activeMessageIndex === -1) return;
    setMessages(prev => {
      const updated = [...prev];
      updated[activeMessageIndex] = { ...updated[activeMessageIndex], [field]: value };
      return updated;
    });
  };

  const handleActiveTemplateSelect = (templateId) => {
    if (activeMessageIndex === -1) return;
    const tpl = templates.find(t => String(t.id) === String(templateId));
    const bodyText = extractTemplateBodyText(tpl);
    const detectedVars = extractTemplateVariables(bodyText);
    const detectedButtons = extractTemplateButtons(tpl);

    setMessages(prev => {
      const updated = [...prev];
      const currentMsg = { ...updated[activeMessageIndex] };
      currentMsg.templateId = templateId;

      const newVars = {};
      detectedVars.forEach(v => {
        newVars[v] = currentMsg.bodyVariables?.[v] || '';
      });
      currentMsg.bodyVariables = newVars;

      const newActions = { ...(currentMsg.buttonActions || {}) };
      detectedButtons.forEach(btnText => {
        if (!newActions[btnText]) {
          newActions[btnText] = {
            action: 'CONTINUE',
            category: 'INTERESTED',
            followUpTemplateId: ''
          };
        }
      });
      currentMsg.buttonActions = newActions;
      updated[activeMessageIndex] = currentMsg;
      return updated;
    });
  };

  const handleActiveInsertShortcut = (varKey, shortcutValue) => {
    if (activeMessageIndex === -1) return;
    setMessages(prev => {
      const updated = [...prev];
      const currentMsg = { ...updated[activeMessageIndex] };
      const currentVars = { ...(currentMsg.bodyVariables || {}) };
      const prevVal = currentVars[varKey] || '';
      currentVars[varKey] = prevVal ? `${prevVal} ${shortcutValue}` : shortcutValue;
      currentMsg.bodyVariables = currentVars;
      updated[activeMessageIndex] = currentMsg;
      return updated;
    });
  };

  const handleActiveAddVariableField = () => {
    if (activeMessageIndex === -1) return;
    setMessages(prev => {
      const updated = [...prev];
      const currentMsg = { ...updated[activeMessageIndex] };
      const currentVars = { ...(currentMsg.bodyVariables || {}) };
      const existingKeys = Object.keys(currentVars);
      const numericKeys = existingKeys.map(k => Number(k)).filter(n => !isNaN(n));
      const nextKey = numericKeys.length > 0 ? String(Math.max(...numericKeys) + 1) : String(existingKeys.length + 1);
      currentVars[nextKey] = '';
      currentMsg.bodyVariables = currentVars;
      updated[activeMessageIndex] = currentMsg;
      return updated;
    });
  };

  const handleActiveRemoveVariableField = (varKey) => {
    if (activeMessageIndex === -1) return;
    setMessages(prev => {
      const updated = [...prev];
      const currentMsg = { ...updated[activeMessageIndex] };
      const currentVars = { ...(currentMsg.bodyVariables || {}) };
      delete currentVars[varKey];
      currentMsg.bodyVariables = currentVars;
      updated[activeMessageIndex] = currentMsg;
      return updated;
    });
  };

  const handleActiveAddButtonAction = () => {
    if (activeMessageIndex === -1) return;
    const btnName = prompt('Enter template button text (e.g. Stop promotions, Call Us, View Services):');
    if (!btnName || !btnName.trim()) return;
    const trimmed = btnName.trim();
    setMessages(prev => {
      const updated = [...prev];
      const currentMsg = { ...updated[activeMessageIndex] };
      const currentActions = { ...(currentMsg.buttonActions || {}) };
      if (!currentActions[trimmed]) {
        currentActions[trimmed] = {
          action: 'CONTINUE',
          category: 'INTERESTED',
          followUpTemplateId: ''
        };
      }
      currentMsg.buttonActions = currentActions;
      updated[activeMessageIndex] = currentMsg;
      return updated;
    });
  };

  const handleActiveRemoveButtonAction = (btnName) => {
    if (activeMessageIndex === -1) return;
    setMessages(prev => {
      const updated = [...prev];
      const currentMsg = { ...updated[activeMessageIndex] };
      const currentActions = { ...(currentMsg.buttonActions || {}) };
      delete currentActions[btnName];
      currentMsg.buttonActions = currentActions;
      updated[activeMessageIndex] = currentMsg;
      return updated;
    });
  };

  const handleActiveUpdateButtonAction = (btnName, field, value) => {
    if (activeMessageIndex === -1) return;
    setMessages(prev => {
      const updated = [...prev];
      const currentMsg = { ...updated[activeMessageIndex] };
      const currentActions = { ...(currentMsg.buttonActions || {}) };
      const actionItem = currentActions[btnName] || { action: 'CONTINUE', category: 'INTERESTED' };
      currentActions[btnName] = { ...actionItem, [field]: value };
      currentMsg.buttonActions = currentActions;
      updated[activeMessageIndex] = currentMsg;
      return updated;
    });
  };

  // ── Step 2 Nested Reminders Handlers ──
  const handleAddReminder = (parentMsgId, delayValue = 1, delayUnit = 'hours') => {
    const reminderId = `rem_${Date.now()}`;
    const newReminder = {
      id: reminderId,
      delayValue: delayValue || 1,
      delayUnit: delayUnit || 'hours', // 'minutes' | 'hours' | 'days'
      skipIfReplied: true,
      templateId: '',
      headerVariable: '',
      bodyVariables: {},
      buttonActions: {}
    };

    setMessages(prev => prev.map(m => {
      if (m.id !== parentMsgId) return m;
      return {
        ...m,
        reminders: [...(m.reminders || []), newReminder]
      };
    }));

    setActiveSelection({
      type: 'REMINDER',
      messageId: parentMsgId,
      reminderId: reminderId
    });

    const unitLabel = delayUnit === 'minutes' ? (delayValue === 1 ? 'min' : 'mins') : delayUnit === 'hours' ? (delayValue === 1 ? 'hour' : 'hours') : (delayValue === 1 ? 'day' : 'days');
    showNotification(`Added reminder (${delayValue} ${unitLabel} after message)!`, 'success');
  };

  const handleRemoveReminder = (parentMsgId, reminderId) => {
    setMessages(prev => prev.map(m => {
      if (m.id !== parentMsgId) return m;
      return {
        ...m,
        reminders: (m.reminders || []).filter(r => r.id !== reminderId)
      };
    }));

    if (activeSelection.reminderId === reminderId) {
      setActiveSelection({
        type: 'MESSAGE',
        messageId: parentMsgId,
        reminderId: null
      });
    }
    showNotification('Reminder removed', 'info');
  };

  const handleUpdateActiveReminder = (field, value) => {
    if (!activeReminder || !activeMessage) return;
    setMessages(prev => prev.map(m => {
      if (m.id !== activeMessage.id) return m;
      return {
        ...m,
        reminders: (m.reminders || []).map(r => {
          if (r.id !== activeReminder.id) return r;
          return { ...r, [field]: value };
        })
      };
    }));
  };

  const handleActiveReminderTemplateSelect = (templateId) => {
    if (!activeReminder || !activeMessage) return;
    const tpl = templates.find(t => String(t.id) === String(templateId));
    const bodyText = extractTemplateBodyText(tpl);
    const detectedVars = extractTemplateVariables(bodyText);
    const detectedButtons = extractTemplateButtons(tpl);

    setMessages(prev => prev.map(m => {
      if (m.id !== activeMessage.id) return m;
      return {
        ...m,
        reminders: (m.reminders || []).map(r => {
          if (r.id !== activeReminder.id) return r;
          const newVars = {};
          detectedVars.forEach(v => {
            newVars[v] = r.bodyVariables?.[v] || '';
          });
          const newActions = { ...(r.buttonActions || {}) };
          detectedButtons.forEach(btnText => {
            if (!newActions[btnText]) {
              newActions[btnText] = {
                action: 'CONTINUE',
                category: 'INTERESTED',
                followUpTemplateId: ''
              };
            }
          });
          return {
            ...r,
            templateId,
            bodyVariables: newVars,
            buttonActions: newActions
          };
        })
      };
    }));
  };

  const handleActiveReminderInsertShortcut = (varKey, shortcutValue) => {
    if (!activeReminder || !activeMessage) return;
    setMessages(prev => prev.map(m => {
      if (m.id !== activeMessage.id) return m;
      return {
        ...m,
        reminders: (m.reminders || []).map(r => {
          if (r.id !== activeReminder.id) return r;
          const currentVars = { ...(r.bodyVariables || {}) };
          const prevVal = currentVars[varKey] || '';
          currentVars[varKey] = prevVal ? `${prevVal} ${shortcutValue}` : shortcutValue;
          return { ...r, bodyVariables: currentVars };
        })
      };
    }));
  };

  const handleActiveReminderAddVariableField = () => {
    if (!activeReminder || !activeMessage) return;
    setMessages(prev => prev.map(m => {
      if (m.id !== activeMessage.id) return m;
      return {
        ...m,
        reminders: (m.reminders || []).map(r => {
          if (r.id !== activeReminder.id) return r;
          const currentVars = { ...(r.bodyVariables || {}) };
          const existingKeys = Object.keys(currentVars);
          const numericKeys = existingKeys.map(k => Number(k)).filter(n => !isNaN(n));
          const nextKey = numericKeys.length > 0 ? String(Math.max(...numericKeys) + 1) : String(existingKeys.length + 1);
          currentVars[nextKey] = '';
          return { ...r, bodyVariables: currentVars };
        })
      };
    }));
  };

  const handleActiveReminderRemoveVariableField = (varKey) => {
    if (!activeReminder || !activeMessage) return;
    setMessages(prev => prev.map(m => {
      if (m.id !== activeMessage.id) return m;
      return {
        ...m,
        reminders: (m.reminders || []).map(r => {
          if (r.id !== activeReminder.id) return r;
          const currentVars = { ...(r.bodyVariables || {}) };
          delete currentVars[varKey];
          return { ...r, bodyVariables: currentVars };
        })
      };
    }));
  };

  const handleActiveReminderAddButtonAction = () => {
    if (!activeReminder || !activeMessage) return;
    const btnName = prompt('Enter template button text:');
    if (!btnName || !btnName.trim()) return;
    const trimmed = btnName.trim();
    setMessages(prev => prev.map(m => {
      if (m.id !== activeMessage.id) return m;
      return {
        ...m,
        reminders: (m.reminders || []).map(r => {
          if (r.id !== activeReminder.id) return r;
          const currentActions = { ...(r.buttonActions || {}) };
          if (!currentActions[trimmed]) {
            currentActions[trimmed] = { action: 'CONTINUE', category: 'INTERESTED', followUpTemplateId: '' };
          }
          return { ...r, buttonActions: currentActions };
        })
      };
    }));
  };

  const handleActiveReminderRemoveButtonAction = (btnName) => {
    if (!activeReminder || !activeMessage) return;
    setMessages(prev => prev.map(m => {
      if (m.id !== activeMessage.id) return m;
      return {
        ...m,
        reminders: (m.reminders || []).map(r => {
          if (r.id !== activeReminder.id) return r;
          const currentActions = { ...(r.buttonActions || {}) };
          delete currentActions[btnName];
          return { ...r, buttonActions: currentActions };
        })
      };
    }));
  };

  const handleActiveReminderUpdateButtonAction = (btnName, field, value) => {
    if (!activeReminder || !activeMessage) return;
    setMessages(prev => prev.map(m => {
      if (m.id !== activeMessage.id) return m;
      return {
        ...m,
        reminders: (m.reminders || []).map(r => {
          if (r.id !== activeReminder.id) return r;
          const currentActions = { ...(r.buttonActions || {}) };
          const actionItem = currentActions[btnName] || { action: 'CONTINUE', category: 'INTERESTED' };
          currentActions[btnName] = { ...actionItem, [field]: value };
          return { ...r, buttonActions: currentActions };
        })
      };
    }));
  };

  // Step 2 Validation & Proceed
  const handleSaveMessagesAndContinue = () => {
    if (messages.length === 0) {
      showNotification('Please configure at least one message', 'error');
      return;
    }
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      if (!msg.templateId) {
        showNotification(`Please select a template for "${msg.title || `Message ${i + 1}`}"`, 'error');
        setActiveSelection({ type: 'MESSAGE', messageId: msg.id, reminderId: null });
        return;
      }
      if (msg.timingType === 'SCHEDULED' && !msg.scheduledAt) {
        showNotification(`Please specify a schedule date & time for "${msg.title || `Message ${i + 1}`}"`, 'error');
        setActiveSelection({ type: 'MESSAGE', messageId: msg.id, reminderId: null });
        return;
      }
      for (let r = 0; r < (msg.reminders || []).length; r++) {
        const rem = msg.reminders[r];
        if (!rem.templateId) {
          showNotification(`Select a template for every reminder (${msg.title || `Message ${i + 1}`} reminder #${r + 1})`, 'error');
          setActiveSelection({ type: 'REMINDER', messageId: msg.id, reminderId: rem.id });
          return;
        }
      }
    }
    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Step 3 Finish / Save Campaign
  const handleFinishCampaign = async () => {
    try {
      setIsFinishing(true);
      const eligibleContactIds = selectedContactIds.filter(id => {
        const contact = allContacts.find(c => c.id === id || String(c.id) === String(id));
        return contact && !isContactExcluded(contact);
      });
      const contactsPhones = eligibleContactIds.map(id => {
        const contact = allContacts.find(c => c.id === id || String(c.id) === String(id));
        return contact ? (contact.phone || contact.phone_number) : null;
      }).filter(Boolean);

      const firstMsg = messages[0] || {};
      const payloadVariables = { ...(firstMsg.bodyVariables || {}) };
      if (firstMsg.headerVariable) {
        payloadVariables['__header_media_url__'] = firstMsg.headerVariable;
      }

      // Flatten primary messages and child reminders with parent_index linkage
      const flattenedSchedules = [];
      messages.forEach((m, primaryIdx) => {
        const primaryScheduleIndex = flattenedSchedules.length;
        flattenedSchedules.push({
          title: m.title || `Message ${primaryIdx + 1}`,
          template_id: m.templateId ? Number(m.templateId) : null,
          scheduled_at: m.scheduledAt ? new Date(m.scheduledAt).toISOString() : null,
          timing_type: m.timingType || 'SCHEDULED',
          relative_delay_minutes: 0,
          parent_index: null,
          skip_if_replied: false,
          variables: m.bodyVariables || {},
          header_variable: m.headerVariable || '',
          button_actions: m.buttonActions || {}
        });

        (m.reminders || []).forEach(rem => {
          let delayMins = Number(rem.delayValue) || 60;
          if (rem.delayUnit === 'hours') delayMins = delayMins * 60;
          if (rem.delayUnit === 'days') delayMins = delayMins * 1440;

          let reminderSendAt = null;
          if (m.scheduledAt) {
            reminderSendAt = new Date(addMinutesToDateTimeLocal(m.scheduledAt, delayMins)).toISOString();
          }

          const unitSingular = rem.delayUnit === 'minutes' ? 'min' : rem.delayUnit === 'hours' ? 'hour' : 'day';
          const unitPlural = rem.delayUnit === 'minutes' ? 'mins' : rem.delayUnit === 'hours' ? 'hours' : 'days';
          const formattedUnit = Number(rem.delayValue) === 1 ? unitSingular : unitPlural;

          flattenedSchedules.push({
            title: `${m.title || `Message ${primaryIdx + 1}`} Reminder (+${rem.delayValue} ${formattedUnit})`,
            template_id: rem.templateId ? Number(rem.templateId) : null,
            scheduled_at: reminderSendAt,
            timing_type: 'RELATIVE',
            relative_delay_minutes: delayMins,
            parent_index: primaryScheduleIndex,
            skip_if_replied: rem.skipIfReplied !== false,
            variables: rem.bodyVariables || {},
            header_variable: rem.headerVariable || '',
            button_actions: rem.buttonActions || {}
          });
        });
      });

      const payload = {
        id: editCampaignDraftId ? Number(editCampaignDraftId) : null,
        name: campaignName.trim(),
        description: description.trim(),
        template_id: firstMsg.templateId ? Number(firstMsg.templateId) : null,
        contacts: contactsPhones,
        variables: payloadVariables,
        scheduled_at: firstMsg.scheduledAt ? new Date(firstMsg.scheduledAt).toISOString() : null,
        is_active: isActive,
        time_zone: timeZone,
        schedules: flattenedSchedules,
        exclude_already_sent: excludeAlreadySent,
        exclude_all_campaigns: excludeAlreadySent && excludeAllCampaigns,
        excluded_campaign_ids: (excludeAlreadySent && !excludeAllCampaigns) ? selectedExcludeCampaignIds : [],
        override_contact_ids: Array.from(overrideAllowedContactIds).map(Number).filter(Boolean)
      };

      if (isActive) {
        await launchCampaignNew(payload);
        showSuccess('Campaign launched and scheduled successfully!');
      } else {
        await createDraftCampaign(payload);
        showSuccess('Campaign saved as draft!');
      }

      localStorage.removeItem('editCampaignDraftId');
      if (onSaved) onSaved();
      handleGoBack();
    } catch (err) {
      showError(getApiErrorMessage(err, 'Failed to save campaign'));
    } finally {
      setIsFinishing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading campaign builder...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1020px', margin: '0 auto', padding: '24px 20px 80px 20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#1e293b' }}>
      {/* Floating Notification Toast */}
      {localToast && (
        <div 
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            zIndex: 99999,
            padding: '12px 18px',
            borderRadius: '10px',
            background: localToast.type === 'error' ? '#ef4444' : '#10b981',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '13.5px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            maxWidth: '480px'
          }}
        >
          <span style={{ fontSize: '16px' }}>{localToast.type === 'error' ? '⚠️' : '✅'}</span>
          <span style={{ flex: 1, lineHeight: '1.4' }}>{localToast.message}</span>
          <button
            type="button"
            onClick={() => setLocalToast(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '0 4px',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Top Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
            {editCampaignDraftId ? `Edit: ${campaignName || 'Campaign'}` : (campaignName ? `Campaign: ${campaignName}` : 'New Campaign')}
          </h1>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Set it up once, here. No pop-ups.
          </p>
        </div>
        <button
          type="button"
          onClick={handleGoBack}
          style={{
            background: '#ffffff',
            border: '1px solid #cbd5e1',
            color: '#334155',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.background = '#f8fafc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#ffffff'; }}
        >
          ← All campaigns
        </button>
      </div>

      {/* ── 3-Step Wizard Navigation ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', margin: '0 0 24px 0' }}>
        {/* Step 1 */}
        <div 
          onClick={() => setCurrentStep(1)}
          style={{
            padding: '14px 18px',
            borderRadius: '12px',
            border: currentStep === 1 ? '2px solid #6366f1' : '1px solid #e2e8f0',
            background: currentStep === 1 ? '#f5f3ff' : '#ffffff',
            cursor: 'pointer',
            boxShadow: currentStep === 1 ? '0 4px 12px rgba(99, 102, 241, 0.1)' : '0 1px 2px rgba(0,0,0,0.02)',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: 800, color: currentStep === 1 ? '#6366f1' : '#94a3b8', letterSpacing: '0.5px' }}>
            STEP 1
          </div>
          <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: '2px 0 2px 0' }}>
            Setup
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Details, audience, exclusions
          </div>
        </div>

        {/* Step 2 */}
        <div 
          onClick={() => {
            if (!campaignName.trim()) {
              showNotification('Please enter a campaign name first', 'error');
              return;
            }
            setCurrentStep(2);
          }}
          style={{
            padding: '14px 18px',
            borderRadius: '12px',
            border: currentStep === 2 ? '2px solid #6366f1' : '1px solid #e2e8f0',
            background: currentStep === 2 ? '#f5f3ff' : '#ffffff',
            cursor: 'pointer',
            boxShadow: currentStep === 2 ? '0 4px 12px rgba(99, 102, 241, 0.1)' : '0 1px 2px rgba(0,0,0,0.02)',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: 800, color: currentStep === 2 ? '#6366f1' : '#94a3b8', letterSpacing: '0.5px' }}>
            STEP 2
          </div>
          <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: '2px 0 2px 0' }}>
            Messages
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Templates, times, actions
          </div>
        </div>

        {/* Step 3 */}
        <div 
          onClick={() => {
            if (!campaignName.trim()) {
              showNotification('Please enter a campaign name first', 'error');
              return;
            }
            setCurrentStep(3);
          }}
          style={{
            padding: '14px 18px',
            borderRadius: '12px',
            border: currentStep === 3 ? '2px solid #6366f1' : '1px solid #e2e8f0',
            background: currentStep === 3 ? '#f5f3ff' : '#ffffff',
            cursor: 'pointer',
            boxShadow: currentStep === 3 ? '0 4px 12px rgba(99, 102, 241, 0.1)' : '0 1px 2px rgba(0,0,0,0.02)',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: 800, color: currentStep === 3 ? '#6366f1' : '#94a3b8', letterSpacing: '0.5px' }}>
            STEP 3
          </div>
          <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: '2px 0 2px 0' }}>
            Review
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Check and finish
          </div>
        </div>
      </div>

      {/* ───────────────────────────────────────────────────────────── */}
      {/* STEP 1: SETUP (DETAILS, AUDIENCE, EXCLUSIONS)                */}
      {/* ───────────────────────────────────────────────────────────── */}
      {currentStep === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* ── Card 1: Campaign details ── */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '22px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: '0 0 16px 0' }}>
              Campaign details
            </h2>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Campaign name *
              </label>
              <input
                id="campaign-name-input"
                type="text"
                placeholder="e.g. October Masterclass"
                value={campaignName}
                onChange={e => {
                  setCampaignName(e.target.value);
                  if (e.target.value.trim()) setFieldErrors(prev => ({ ...prev, name: null }));
                }}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 14px',
                  border: fieldErrors.name ? '2px solid #ef4444' : '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: fieldErrors.name ? '#fef2f2' : '#ffffff',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={e => e.target.style.borderColor = '#6366f1'}
                onBlur={e => e.target.style.borderColor = fieldErrors.name ? '#ef4444' : '#cbd5e1'}
              />
              {fieldErrors.name && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>⚠️</span> {fieldErrors.name}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Description
              </label>
              <textarea
                rows={3}
                placeholder="What is this campaign about?"
                value={description}
                onChange={e => setDescription(e.target.value)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 14px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={e => e.target.style.borderColor = '#6366f1'}
                onBlur={e => e.target.style.borderColor = '#cbd5e1'}
              />
            </div>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer' }}
              />
              Active (messages send on schedule)
            </label>
          </div>

          {/* ── Card 2: Audience ── */}
          <div 
            id="audience-card-section"
            style={{ 
              background: '#ffffff', 
              border: fieldErrors.audience ? '2px solid #ef4444' : '1px solid #e2e8f0', 
              borderRadius: '14px', 
              padding: '22px 24px', 
              boxShadow: fieldErrors.audience ? '0 0 0 4px rgba(239, 68, 68, 0.1)' : '0 1px 3px rgba(0,0,0,0.02)',
              transition: 'all 0.2s ease'
            }}
          >
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: '0 0 2px 0' }}>
              Audience
            </h2>
            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 16px 0' }}>
              Who should this campaign go to? You can remove duplicates in the next section.
            </p>

            {fieldErrors.audience && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '10px 14px',
                marginBottom: '16px',
                color: '#b91c1c',
                fontSize: '13px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>⚠️</span>
                <span>{fieldErrors.audience}</span>
              </div>
            )}

            {/* Audience Tabs */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setAudienceTab('manual')}
                style={{
                  padding: '7px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: audienceTab === 'manual' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                  background: audienceTab === 'manual' ? '#eff6ff' : '#ffffff',
                  color: audienceTab === 'manual' ? '#1d4ed8' : '#475569',
                  transition: 'all 0.15s ease'
                }}
              >
                Manual selection
              </button>
              <button
                type="button"
                onClick={() => setAudienceTab('groups')}
                style={{
                  padding: '7px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: audienceTab === 'groups' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                  background: audienceTab === 'groups' ? '#eff6ff' : '#ffffff',
                  color: audienceTab === 'groups' ? '#1d4ed8' : '#475569',
                  transition: 'all 0.15s ease'
                }}
              >
                👥 From contact group
              </button>
              <button
                type="button"
                onClick={() => setAudienceTab('replies')}
                style={{
                  padding: '7px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: audienceTab === 'replies' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                  background: audienceTab === 'replies' ? '#eff6ff' : '#ffffff',
                  color: audienceTab === 'replies' ? '#1d4ed8' : '#475569',
                  transition: 'all 0.15s ease'
                }}
              >
                🎯 From campaign replies
              </button>
            </div>

            {/* Sub-view: Manual Selection */}
            {audienceTab === 'manual' && (
              <div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '220px', position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Search name or phone"
                      value={contactSearchQuery}
                      onChange={e => setContactSearchQuery(e.target.value)}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '8px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <select
                    value={selectedGroupFilter}
                    onChange={e => setSelectedGroupFilter(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#334155',
                      background: '#ffffff',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">All groups</option>
                    {contactGroups.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={handleSelectAllShown}
                    style={{
                      padding: '8px 14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      background: '#ffffff',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      color: '#334155',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {visibleContacts.length > 0 && visibleContacts.every(c => selectedContactIds.includes(c.id))
                      ? `Deselect all shown (${visibleContacts.length})`
                      : `Select all shown (${visibleContacts.length})`}
                  </button>
                </div>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '260px', overflowY: 'auto', background: '#fafafa' }}>
                  {visibleContacts.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                      No contacts found matching your criteria.
                    </div>
                  ) : (
                    visibleContacts.map(contact => {
                      const isSelected = selectedContactIds.includes(contact.id);
                      const isOriginallyByCampaign = excludeAlreadySent && (
                        (contact.id && (campaignExcludedContactIds.has(contact.id) || campaignExcludedContactIds.has(Number(contact.id)) || campaignExcludedContactIds.has(String(contact.id)))) ||
                        (contact.phone && (campaignExcludedPhoneNumbers.has(String(contact.phone).replace(/\D/g, '')) || campaignExcludedPhoneNumbers.has(String(contact.phone).replace(/\D/g, '').slice(-10))))
                      );
                      const isReIncluded = overrideAllowedContactIds.has(contact.id) || overrideAllowedContactIds.has(Number(contact.id)) || overrideAllowedContactIds.has(String(contact.id));
                      const isByCamp = isOriginallyByCampaign && !isReIncluded;
                      const isByGrp = isContactInAnyExcludedGroup(contact);
                      const isIndiv = individualExcludedContactIds.has(contact.id);
                      const isExcl = isByCamp || isByGrp || isIndiv;

                      return (
                        <div
                          key={contact.id}
                          onClick={() => toggleContactSelection(contact.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '9px 14px',
                            borderBottom: '1px solid #f1f5f9',
                            background: isReIncluded ? '#f0fdf4' : (isExcl ? 'rgba(239, 68, 68, 0.04)' : (isSelected ? '#f8fafc' : '#ffffff')),
                            opacity: isByGrp ? 0.6 : 1,
                            cursor: isByGrp ? 'not-allowed' : 'pointer',
                            transition: 'background 0.15s ease'
                          }}
                          title={isByCamp ? 'Click to re-include this contact and send' : ''}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isByGrp}
                              onChange={() => {}}
                              style={{ width: '15px', height: '15px', accentColor: '#2563eb', cursor: isByGrp ? 'not-allowed' : 'pointer' }}
                            />
                            <div>
                              <span style={{ 
                                fontSize: '13.5px', 
                                fontWeight: 600, 
                                color: isReIncluded ? '#15803d' : (isExcl ? '#94a3b8' : '#0f172a'),
                                textDecoration: isExcl ? 'line-through' : 'none'
                              }}>
                                {contact.name || 'Unnamed Contact'}
                              </span>
                              <span style={{ fontSize: '12.5px', color: '#64748b', marginLeft: '12px', fontFamily: 'monospace' }}>
                                {contact.phone_number || contact.phone || ''}
                              </span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isReIncluded && (
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: '4px' }}>
                                ✓ Re-included (Will Send)
                              </span>
                            )}
                            {!isReIncluded && isByCamp && (
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px' }} title="Click to re-include & send">
                                Already Sent ↻
                              </span>
                            )}
                            {!isByCamp && isByGrp && (
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px' }}>
                                Group Excluded
                              </span>
                            )}
                            {!isOriginallyByCampaign && !isByGrp && isIndiv && (
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px' }}>
                                Excluded
                              </span>
                            )}
                            {contact.group_name && (
                              <span style={{ fontSize: '11px', color: '#475569', background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>
                                {contact.group_name}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Sub-view: From Contact Group */}
            {audienceTab === 'groups' && (
              <div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
                  <select
                    value={sourceGroupId}
                    onChange={e => {
                      const gid = e.target.value;
                      setSourceGroupId(gid);
                      if (gid) {
                        const gContacts = allContacts.filter(c => {
                          if (Array.isArray(c.groups)) {
                            return c.groups.some(g => (typeof g === 'object' ? g?.id === Number(gid) : g === Number(gid)));
                          }
                          return c.group === Number(gid) || c.group?.id === Number(gid);
                        });
                        const gIds = gContacts.map(c => c.id);
                        setSelectedContactIds(prev => Array.from(new Set([...prev, ...gIds])));
                        setFieldErrors(prev => ({ ...prev, audience: null }));
                      }
                    }}
                    style={{
                      flex: 1,
                      minWidth: '240px',
                      padding: '9px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '13px',
                      outline: 'none',
                      background: '#ffffff'
                    }}
                  >
                    <option value="">Select a contact group...</option>
                    {contactGroups.map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.contacts_count ?? allContacts.filter(c => Array.isArray(c.groups) ? c.groups.some(cg => (typeof cg === 'object' ? cg?.id === g.id : cg === g.id)) : c.group === g.id).length} contacts)
                      </option>
                    ))}
                  </select>

                  {sourceGroupId && (
                    <>
                      <button
                        type="button"
                        onClick={handleSelectGroupContacts}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: 'none',
                          background: '#2563eb',
                          color: '#ffffff',
                          fontSize: '12.5px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        + Add all from group ({groupAudienceContacts.length})
                      </button>
                      <button
                        type="button"
                        onClick={handleDeselectGroupContacts}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1',
                          background: '#ffffff',
                          color: '#475569',
                          fontSize: '12.5px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Deselect group
                      </button>
                    </>
                  )}
                </div>

                {sourceGroupId && (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                    {groupAudienceContacts.map(c => {
                      const isSelected = selectedContactIds.includes(c.id);
                      const isOriginallyByCampaign = excludeAlreadySent && (
                        (c.id && (campaignExcludedContactIds.has(c.id) || campaignExcludedContactIds.has(Number(c.id)) || campaignExcludedContactIds.has(String(c.id)))) ||
                        (c.phone && (campaignExcludedPhoneNumbers.has(String(c.phone).replace(/\D/g, '')) || campaignExcludedPhoneNumbers.has(String(c.phone).replace(/\D/g, '').slice(-10))))
                      );
                      const isReIncluded = overrideAllowedContactIds.has(c.id) || overrideAllowedContactIds.has(Number(c.id)) || overrideAllowedContactIds.has(String(c.id));
                      const isByCamp = isOriginallyByCampaign && !isReIncluded;
                      const isByGrp = isContactInAnyExcludedGroup(c);
                      const isIndiv = individualExcludedContactIds.has(c.id);
                      const isExcl = isByCamp || isByGrp || isIndiv;

                      return (
                        <div
                          key={c.id}
                          onClick={() => {
                            if (isByGrp) {
                              showNotification('This contact belongs to an excluded group and cannot be selected', 'error');
                              return;
                            }
                            toggleContactSelection(c.id);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 14px',
                            borderBottom: '1px solid #f1f5f9',
                            background: isReIncluded ? '#f0fdf4' : (isExcl ? 'rgba(239, 68, 68, 0.04)' : (isSelected ? '#f8fafc' : '#ffffff')),
                            opacity: isByGrp ? 0.6 : 1,
                            cursor: isByGrp ? 'not-allowed' : 'pointer',
                            transition: 'background 0.15s ease'
                          }}
                          title={isByCamp ? 'Click to re-include this contact and send' : ''}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isByGrp}
                              onChange={() => {}}
                              style={{ width: '15px', height: '15px', accentColor: '#2563eb', cursor: isByGrp ? 'not-allowed' : 'pointer' }}
                            />
                            <span style={{ 
                              fontSize: '13px', 
                              fontWeight: 600, 
                              color: isReIncluded ? '#15803d' : (isExcl ? '#94a3b8' : '#0f172a'),
                              textDecoration: isExcl ? 'line-through' : 'none'
                            }}>
                              {c.name}
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b', fontFamily: 'monospace' }}>
                              {c.phone_number || c.phone}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isReIncluded && (
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: '4px' }}>
                                ✓ Re-included (Will Send)
                              </span>
                            )}
                            {!isReIncluded && isByCamp && (
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px' }} title="Click to re-include & send">
                                Already Sent ↻
                              </span>
                            )}
                            {!isByCamp && isByGrp && (
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px' }}>
                                Group Excluded
                              </span>
                            )}
                            {!isOriginallyByCampaign && !isByGrp && isIndiv && (
                              <span style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px' }}>
                                Excluded
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Sub-view: From Campaign Replies */}
            {audienceTab === 'replies' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                      Source campaign
                    </label>
                    <select
                      value={sourceCampaignId}
                      onChange={e => setSourceCampaignId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '13px',
                        background: '#ffffff',
                        outline: 'none'
                      }}
                    >
                      <option value="">Select past campaign...</option>
                      {pastCampaigns.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                      Reply Category
                    </label>
                    <select
                      value={sourceCategory}
                      onChange={e => setSourceCategory(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '13px',
                        background: '#ffffff',
                        outline: 'none'
                      }}
                    >
                      {REPLY_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {sourceCampaignId && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '12.5px', color: '#64748b' }}>
                      Found {sourceReplyContacts.length} contacts with reply "{REPLY_CATEGORIES.find(r => r.value === sourceCategory)?.label}"
                    </span>
                    <button
                      type="button"
                      onClick={handleSelectReplyContacts}
                      disabled={sourceReplyContacts.length === 0}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#2563eb',
                        color: '#ffffff',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: sourceReplyContacts.length === 0 ? 'not-allowed' : 'pointer',
                        opacity: sourceReplyContacts.length === 0 ? 0.6 : 1
                      }}
                    >
                      + Add these contacts to audience
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ 
              marginTop: '14px', 
              fontSize: '13px', 
              fontWeight: 700, 
              color: selectedContactIds.length > 0 ? '#334155' : '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <span>{selectedContactIds.length} contacts selected</span>
              {selectedContactIds.length === 0 && (
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#dc2626' }}>
                  (Please pick at least 1 contact above using checkboxes or choose a group)
                </span>
              )}
            </div>
          </div>

          {/* ── Card 3: Exclude people ── */}
          <div 
            id="exclude-card-section"
            style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '22px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}
          >
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: '0 0 2px 0' }}>
              Exclude people
            </h2>
            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 16px 0' }}>
              Avoid sending the same person twice: remove anyone already reached by another campaign, in a group, or picked individually.
            </p>

            {/* Sub-section 1: Exclude Already Sent in Campaign Toggle Box */}
            <div 
              style={{
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                background: '#fafafa',
                padding: '16px 18px',
                marginBottom: '18px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#0f172a' }}>
                    Exclude Already Sent in Campaign
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    Do not send to contacts who already received messages from previous campaigns
                  </div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={excludeAlreadySent}
                    onChange={(e) => setExcludeAlreadySent(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: excludeAlreadySent ? '#2563eb' : '#cbd5e1',
                    borderRadius: '24px', transition: '0.2s ease',
                  }}>
                    <span style={{
                      position: 'absolute',
                      height: '18px', width: '18px',
                      left: excludeAlreadySent ? '22px' : '3px',
                      bottom: '3px',
                      background: '#ffffff',
                      borderRadius: '50%',
                      transition: '0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }} />
                  </span>
                </label>
              </div>

              {excludeAlreadySent && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setExcludeAllCampaigns(true)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '8px',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: excludeAllCampaigns ? '2px solid #2563eb' : '1px solid #cbd5e1',
                        background: excludeAllCampaigns ? '#eff6ff' : '#ffffff',
                        color: excludeAllCampaigns ? '#1d4ed8' : '#475569',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      All Previous Campaigns
                    </button>
                    <button
                      type="button"
                      onClick={() => setExcludeAllCampaigns(false)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '8px',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: !excludeAllCampaigns ? '2px solid #2563eb' : '1px solid #cbd5e1',
                        background: !excludeAllCampaigns ? '#eff6ff' : '#ffffff',
                        color: !excludeAllCampaigns ? '#1d4ed8' : '#475569',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      Specific Campaign(s)
                    </button>
                  </div>

                  {!excludeAllCampaigns && (
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', background: '#ffffff', maxHeight: '160px', overflowY: 'auto' }}>
                      {pastCampaigns.length === 0 ? (
                        <div style={{ fontSize: '12.5px', color: '#94a3b8' }}>No past campaigns found.</div>
                      ) : (
                        pastCampaigns.map(camp => (
                          <label key={camp.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', fontSize: '13px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selectedExcludeCampaignIds.includes(camp.id)}
                              onChange={() => toggleExcludeCampaign(camp.id)}
                              style={{ accentColor: '#2563eb' }}
                            />
                            <span style={{ fontWeight: 600, color: '#334155' }}>{camp.name}</span>
                            <span style={{ fontSize: '11.5px', color: '#64748b' }}>({camp.status || 'Past'})</span>
                          </label>
                        ))
                      )}
                    </div>
                  )}

                  {isCheckingCampaignExclusions ? (
                    <div style={{ fontSize: '12px', color: '#6366f1', fontStyle: 'italic' }}>
                      Checking excluded contacts...
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
                      ✓ {campaignExcludedContactIds.size} contact{campaignExcludedContactIds.size === 1 ? '' : 's'} identified for exclusion from previous campaigns
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sub-section 2: Contact groups to exclude */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                Contact groups to exclude
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {contactGroups.map(grp => {
                  const isExcluded = excludedGroupIds.has(grp.id);
                  return (
                    <button
                      key={grp.id}
                      type="button"
                      onClick={() => toggleExcludeGroup(grp.id)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: isExcluded ? '1.5px solid #ef4444' : '1px solid #cbd5e1',
                        background: isExcluded ? '#fee2e2' : '#ffffff',
                        color: isExcluded ? '#b91c1c' : '#475569',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {isExcluded ? '✕' : '+'} {grp.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sub-section 3: Individual contacts to exclude */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                  Individual contacts to exclude ({individualExcludedContactIds.size})
                  {campaignExcludedContactIds.size > 0 && (
                    <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 600, marginLeft: '8px' }}>
                      ({Math.max(0, campaignExcludedContactIds.size - overrideAllowedContactIds.size)} already excluded from previous campaigns
                      {overrideAllowedContactIds.size > 0 ? `, ${overrideAllowedContactIds.size} re-included` : ''})
                    </span>
                  )}
                </div>
                {visibleExclusionContacts.length > 0 && (
                  <button
                    type="button"
                    onClick={handleToggleSelectAllExclusion}
                    style={{
                      padding: '5px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      background: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#334155',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  >
                    {visibleExclusionContacts.length > 0 && visibleExclusionContacts.every(c => individualExcludedContactIds.has(c.id))
                      ? `Deselect all (${visibleExclusionContacts.length})`
                      : `Select all shown (${visibleExclusionContacts.length})`}
                  </button>
                )}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="Search name or phone"
                  value={exclusionSearchQuery}
                  onChange={e => setExclusionSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '8px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', background: '#fafafa' }}>
                {visibleExclusionContacts.slice(0, 100).map(contact => {
                  const isOriginallyByCampaign = excludeAlreadySent && (
                    (contact.id && (campaignExcludedContactIds.has(contact.id) || campaignExcludedContactIds.has(Number(contact.id)) || campaignExcludedContactIds.has(String(contact.id)))) ||
                    (contact.phone && (campaignExcludedPhoneNumbers.has(String(contact.phone).replace(/\D/g, '')) || campaignExcludedPhoneNumbers.has(String(contact.phone).replace(/\D/g, '').slice(-10))))
                  );
                  const isReIncluded = overrideAllowedContactIds.has(contact.id) || overrideAllowedContactIds.has(Number(contact.id)) || overrideAllowedContactIds.has(String(contact.id));
                  const isByCampaign = isOriginallyByCampaign && !isReIncluded;
                  const isByGroup = isContactInAnyExcludedGroup(contact);
                  const isExcludedIndividually = individualExcludedContactIds.has(contact.id);
                  const isExcluded = isExcludedIndividually || isByCampaign || isByGroup;

                  return (
                    <div
                      key={contact.id}
                      onClick={() => {
                        if (isByGroup) {
                          showNotification('This contact belongs to an excluded group. Remove the group exclusion above to include them.', 'error');
                          return;
                        }
                        if (isOriginallyByCampaign) {
                          toggleOverrideAllowedContact(contact.id);
                          return;
                        }
                        toggleExcludeIndividualContact(contact.id);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 14px',
                        borderBottom: '1px solid #f1f5f9',
                        background: isReIncluded ? '#f0fdf4' : (isExcluded ? '#fef2f2' : '#ffffff'),
                        cursor: isByGroup ? 'not-allowed' : 'pointer',
                        transition: 'background 0.15s ease'
                      }}
                      title={isOriginallyByCampaign ? (isReIncluded ? 'Click to exclude this contact again' : 'Click to send again to this contact') : ''}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="checkbox"
                          checked={isExcluded}
                          disabled={isByGroup}
                          onChange={() => {
                            if (isByGroup) return;
                            if (isOriginallyByCampaign) {
                              toggleOverrideAllowedContact(contact.id);
                            } else {
                              toggleExcludeIndividualContact(contact.id);
                            }
                          }}
                          style={{
                            width: '15px',
                            height: '15px',
                            accentColor: isReIncluded ? '#16a34a' : '#ef4444',
                            cursor: isByGroup ? 'not-allowed' : 'pointer'
                          }}
                        />
                        <span style={{ 
                          fontSize: '13px', 
                          fontWeight: 600, 
                          color: isReIncluded ? '#15803d' : (isExcluded ? '#b91c1c' : '#0f172a'),
                          textDecoration: (isExcluded && !isReIncluded) ? 'line-through' : 'none'
                        }}>
                          {contact.name || 'Unnamed'}
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748b', fontFamily: 'monospace' }}>
                          {contact.phone_number || contact.phone}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isReIncluded && (
                          <span style={{ fontSize: '11px', color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                            ✓ Re-included (Will Send)
                          </span>
                        )}
                        {!isReIncluded && isByCampaign && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleOverrideAllowedContact(contact.id);
                            }}
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              color: '#dc2626',
                              background: '#fee2e2',
                              border: '1px solid #fca5a5',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#fecaca'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#fee2e2'; }}
                            title="Click to send again to this contact"
                          >
                            Already Sent ↻ <span style={{ fontSize: '10.5px', textDecoration: 'underline', color: '#2563eb' }}>Send Anyway</span>
                          </button>
                        )}
                        {!isByCampaign && isByGroup && (
                          <span style={{ fontSize: '11px', color: '#b91c1c', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                            Group Excluded
                          </span>
                        )}
                        {!isOriginallyByCampaign && !isByGroup && isExcludedIndividually && (
                          <span style={{ fontSize: '11px', color: '#b91c1c', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                            Excluded
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Fixed / Sticky Footer: Live Formula Bar ── */}
          <div 
            style={{
              position: 'sticky',
              bottom: '16px',
              background: '#ffffff',
              padding: '16px 24px',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 100,
              marginTop: '16px'
            }}
          >
            <div>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>
                Final audience
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>
                {selectedContactIds.length} selected – {effectiveExcludedCount} excluded = <span style={{ color: finalWillReceiveCount > 0 ? '#10b981' : (selectedContactIds.length > 0 ? '#ef4444' : '#64748b'), fontWeight: 800, fontSize: '16px' }}>{finalWillReceiveCount} will receive</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleGoBack}
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '13.5px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAndContinueStep1}
                style={{
                  background: '#6366f1',
                  color: '#ffffff',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '13.5px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Save & continue →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* STEP 2: MESSAGES (TEMPLATES, TIMING, SHORTCUTS & ACTIONS)     */}
      {/* ───────────────────────────────────────────────────────────── */}
      {currentStep === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Card: Campaign context, Timezone & Active state */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <div style={{ fontSize: '13.5px', color: '#475569', marginBottom: '16px', lineHeight: 1.5 }}>
              Campaign: <strong style={{ color: '#0f172a' }}>{campaignName || 'Untitled'}</strong> — delivering to <strong style={{ color: '#16a34a' }}>{finalWillReceiveCount}</strong> recipients. 
              Configure your multi-day message sequence below; each template will be delivered at its scheduled date and time.
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '240px' }}>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                  Delivery Time Zone
                </label>
                <input
                  type="text"
                  value={timeZone}
                  onChange={e => setTimeZone(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '9px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#334155',
                    background: '#f8fafc',
                    outline: 'none'
                  }}
                />
              </div>

              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#334155', paddingBottom: '8px' }}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer' }}
                />
                Active (Messages dispatch automatically on schedule)
              </label>
            </div>
          </div>

          {/* ── Two-Column Layout: Left (Message Sequence Cards) + Right (Active Inspector) ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(330px, 390px) 1fr', gap: '22px', alignItems: 'start' }}>
            
            {/* ── Left Column: Sequence Cards ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>
                    Message Sequence ({messages.length})
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    Select a card to configure its parameters
                  </div>
                </div>
              </div>

              {/* Message Cards List */}
              {messages.map((msg, idx) => {
                const isMsgSelected = activeSelection.type === 'MESSAGE' && activeSelection.messageId === msg.id;
                const msgTpl = templates.find(t => String(t.id) === String(msg.templateId));
                const remindersList = msg.reminders || [];

                return (
                  <div
                    key={msg.id}
                    onClick={() => setActiveSelection({ type: 'MESSAGE', messageId: msg.id, reminderId: null })}
                    style={{
                      background: '#ffffff',
                      border: isMsgSelected ? '2px solid #6366f1' : '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      boxShadow: isMsgSelected ? '0 4px 16px rgba(99, 102, 241, 0.15)' : '0 1px 3px rgba(0,0,0,0.03)',
                      transition: 'all 0.18s ease',
                      position: 'relative'
                    }}
                  >
                    {/* Card Top Row: Badge, Title, Timestamp, Actions */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                        <div 
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            background: isMsgSelected ? '#6366f1' : '#4f46e5',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            fontWeight: 800,
                            flexShrink: 0
                          }}
                        >
                          {idx + 1}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: '14.5px', fontWeight: 800, color: isMsgSelected ? '#4338ca' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {msg.title || `Message ${idx + 1}`}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {msg.timingType === 'IMMEDIATE' 
                              ? 'Immediate on launch' 
                              : (msg.scheduledAt ? formatDateTimeDisplay(msg.scheduledAt) : 'Unscheduled')} · {msgTpl ? msgTpl.name : 'No template'}
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px', fontWeight: 600 }}>
                            ✓ 0 sent
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateMessage(msg.id);
                          }}
                          title="Duplicate message"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#64748b',
                            cursor: 'pointer',
                            padding: '4px 6px',
                            borderRadius: '4px',
                            fontSize: '13px'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.color = '#6366f1'; e.currentTarget.style.background = '#f5f3ff'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; }}
                        >
                          ⧉
                        </button>
                        {messages.length > 1 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMessage(msg.id);
                            }}
                            title="Remove message"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#94a3b8',
                              cursor: 'pointer',
                              padding: '4px 6px',
                              borderRadius: '4px',
                              fontSize: '13px'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fee2e2'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Nested Sub-Reminders List */}
                    {remindersList.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                        {remindersList.map((rem, rIdx) => {
                          const isRemSelected = activeSelection.type === 'REMINDER' && activeSelection.reminderId === rem.id;
                          const remTpl = templates.find(t => String(t.id) === String(rem.templateId));
                          const unitSingular = rem.delayUnit === 'minutes' ? 'min' : rem.delayUnit === 'hours' ? 'hour' : 'day';
                          const unitPlural = rem.delayUnit === 'minutes' ? 'mins' : rem.delayUnit === 'hours' ? 'hours' : 'days';
                          const formattedUnit = Number(rem.delayValue) === 1 ? unitSingular : unitPlural;

                          return (
                            <div
                              key={rem.id || rIdx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveSelection({ type: 'REMINDER', messageId: msg.id, reminderId: rem.id });
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                background: isRemSelected ? '#eff6ff' : '#f8fafc',
                                border: isRemSelected ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', minWidth: 0, overflow: 'hidden' }}>
                                <span style={{ color: '#2563eb', fontSize: '13.5px', fontWeight: 800 }}>↳</span>
                                <span style={{ fontWeight: 800, color: '#1e3a8a' }}>
                                  {rem.delayValue} {formattedUnit} later
                                </span>
                                <span style={{ color: '#cbd5e1' }}>·</span>
                                <span style={{ color: remTpl ? '#15803d' : '#64748b', fontWeight: remTpl ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
                                  {remTpl ? remTpl.name : 'No template'}
                                </span>
                                <span style={{ color: '#cbd5e1' }}>·</span>
                                <span style={{ color: '#64748b', fontSize: '12px' }}>
                                  {rem.skipIfReplied !== false ? 'if no reply' : 'always'}
                                </span>
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                {!rem.templateId && (
                                  <span 
                                    title="Template required for this reminder"
                                    style={{
                                      color: '#dc2626',
                                      fontSize: '14px',
                                      fontWeight: 800,
                                      padding: '0 2px'
                                    }}
                                  >
                                    !
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveReminder(msg.id, rem.id);
                                  }}
                                  title="Remove reminder"
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    padding: '2px 4px',
                                    lineHeight: 1
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                                  onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Add Reminder Bar: Chips */}
                    <div 
                      style={{ 
                        borderTop: '1px solid #f1f5f9', 
                        paddingTop: '10px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px', 
                        flexWrap: 'wrap' 
                      }}
                    >
                      <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>
                        Add reminder:
                      </span>
                      {[
                        { label: '+ 30 min', val: 30, unit: 'minutes' },
                        { label: '+ 1 hour', val: 1, unit: 'hours' },
                        { label: '+ 2 hours', val: 2, unit: 'hours' },
                        { label: '+ 1 day', val: 1, unit: 'days' }
                      ].map(chip => (
                        <button
                          key={chip.label}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddReminder(msg.id, chip.val, chip.unit);
                          }}
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #cbd5e1',
                            color: '#1d4ed8',
                            borderRadius: '6px',
                            padding: '3px 8px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.12s ease'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                        >
                          {chip.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Directly adds custom reminder and selects it in the right inspector with NO browser prompt!
                          handleAddReminder(msg.id, 3, 'hours');
                        }}
                        style={{
                          background: '#f8fafc',
                          border: '1px dashed #cbd5e1',
                          color: '#475569',
                          borderRadius: '6px',
                          padding: '3px 8px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: 'all 0.12s ease'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#1d4ed8'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                      >
                        Custom...
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Big "+ Add message (next day)" Button */}
              <button
                type="button"
                onClick={handleAddNextDayMessage}
                style={{
                  background: '#ffffff',
                  border: '2px dashed #6366f1',
                  color: '#6366f1',
                  padding: '14px 18px',
                  borderRadius: '12px',
                  fontSize: '13.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 1px 3px rgba(99, 102, 241, 0.05)',
                  transition: 'all 0.15s ease',
                  marginTop: '4px'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f3ff'; e.currentTarget.style.borderColor = '#4f46e5'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#6366f1'; }}
              >
                <span style={{ fontSize: '18px', lineHeight: 1 }}>+</span> Add message (next day)
              </button>
            </div>

            {/* ── Right Column: Dynamic Inspector (Reminder OR Message) ── */}
            {activeSelection.type === 'REMINDER' && activeReminder ? (
              <div 
                style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                }}
              >
                {/* Reminder Header */}
                <div style={{ marginBottom: '18px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>
                    REMINDER FOR {(activeMessage?.title || `MESSAGE ${activeMessageIndex + 1}`).toUpperCase()}
                  </div>
                  <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '4px 0 0 0' }}>
                    {activeReminder.delayValue} {activeReminder.delayUnit === 'minutes' ? (Number(activeReminder.delayValue) === 1 ? 'minute' : 'minutes') : activeReminder.delayUnit === 'hours' ? (Number(activeReminder.delayValue) === 1 ? 'hour' : 'hours') : (Number(activeReminder.delayValue) === 1 ? 'day' : 'days')} after the message
                  </h3>
                </div>

                {/* Send after the message * */}
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Send after the message *
                  </label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      id="reminder-delay-value-input"
                      type="number"
                      min="1"
                      value={activeReminder.delayValue}
                      onChange={e => handleUpdateActiveReminder('delayValue', Math.max(1, parseInt(e.target.value) || 1))}
                      style={{
                        width: '90px',
                        boxSizing: 'border-box',
                        padding: '9px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        outline: 'none',
                        color: '#0f172a'
                      }}
                      onFocus={e => e.target.style.borderColor = '#2563eb'}
                      onBlur={e => e.target.style.borderColor = '#cbd5e1'}
                    />
                    <select
                      value={activeReminder.delayUnit}
                      onChange={e => handleUpdateActiveReminder('delayUnit', e.target.value)}
                      style={{
                        padding: '9px 14px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: '#334155',
                        background: '#ffffff',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="minutes">minutes</option>
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                  </div>
                </div>

                {/* Checkbox: Skip if the contact already replied */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={activeReminder.skipIfReplied !== false}
                      onChange={e => handleUpdateActiveReminder('skipIfReplied', e.target.checked)}
                      style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer' }}
                    />
                    Skip if the contact already replied
                  </label>
                </div>

                {/* Message template * */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Message template *
                  </label>
                  <select
                    value={activeReminder.templateId || ''}
                    onChange={e => handleActiveReminderTemplateSelect(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: !activeReminder.templateId ? '1px solid #fca5a5' : '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '13.5px',
                      color: activeReminder.templateId ? '#0f172a' : '#94a3b8',
                      background: '#ffffff',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select template</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id} style={{ color: '#0f172a' }}>
                        {t.name} ({t.category || 'MARKETING'})
                      </option>
                    ))}
                  </select>
                  {!activeReminder.templateId && (
                    <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '6px' }}>
                      Select a template for every reminder.
                    </div>
                  )}
                </div>

                {/* Template Variables & Button Actions for this Reminder */}
                {(() => {
                  const selectedTpl = templates.find(t => String(t.id) === String(activeReminder.templateId));
                  if (!selectedTpl) return null;

                  const bodyPreview = extractTemplateBodyText(selectedTpl);
                  const bodyVarKeys = Object.keys(activeReminder.bodyVariables || {});
                  const buttonKeys = Object.keys(activeReminder.buttonActions || {});

                  return (
                    <div 
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #e0e7ff',
                        borderRadius: '12px',
                        padding: '18px 20px',
                        marginTop: '16px'
                      }}
                    >
                      {/* Header: Variables */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#4338ca', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>✏️</span> Variables for Reminder Template "{selectedTpl.name}"
                        </div>
                        <button
                          type="button"
                          onClick={handleActiveReminderAddVariableField}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #cbd5e1',
                            color: '#4338ca',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          + Add Variable Field
                        </button>
                      </div>

                      {/* Body Preview */}
                      {bodyPreview && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#2563eb', letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '4px' }}>
                            TEMPLATE TEXT PREVIEW
                          </div>
                          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', fontSize: '12.5px', color: '#334155', lineHeight: 1.5, maxHeight: '110px', overflowY: 'auto' }}>
                            {bodyPreview}
                          </div>
                        </div>
                      )}

                      {/* Header Variable */}
                      <div style={{ marginBottom: '14px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Header Parameter Value:
                        </label>
                        <input
                          type="text"
                          placeholder="Value for Header Parameter (media URL or text)"
                          value={activeReminder.headerVariable || ''}
                          onChange={e => handleUpdateActiveReminder('headerVariable', e.target.value)}
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 12px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            fontSize: '12.5px',
                            background: '#ffffff',
                            outline: 'none'
                          }}
                        />
                      </div>

                      {/* Body Variables List */}
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                          Body Variables ({bodyVarKeys.length} parameters configured)
                        </div>

                        {bodyVarKeys.length === 0 ? (
                          <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', padding: '6px 0' }}>
                            No body variables found in this template. Click "+ Add Variable Field" to add one manually.
                          </div>
                        ) : (
                          bodyVarKeys.map((varKey, vIdx) => (
                            <div 
                              key={varKey}
                              style={{
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '12px 14px',
                                marginBottom: '10px'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#1e293b' }}>
                                    Variable {"{{"}{isNaN(varKey) ? vIdx + 1 : varKey}{"}}"} ({varKey})
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleActiveReminderRemoveVariableField(varKey)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#ef4444',
                                      fontSize: '11px',
                                      fontWeight: 700,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    ✕ Remove
                                  </button>
                                </div>

                                {/* Shortcut Chips */}
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {[
                                    { label: '+ Contact Name', value: '{{name}}' },
                                    { label: '+ Event Title', value: campaignName || '{{event_title}}' },
                                    { label: '+ Event Date', value: '{{event_date}}' },
                                    { label: '+ Phone', value: '{{phone}}' },
                                  ].map(chip => (
                                    <button
                                      key={chip.label}
                                      type="button"
                                      onClick={() => handleActiveReminderInsertShortcut(varKey, chip.value)}
                                      style={{
                                        background: '#f1f5f9',
                                        border: '1px solid #cbd5e1',
                                        color: '#2563eb',
                                        padding: '3px 8px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                      }}
                                    >
                                      {chip.label}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <input
                                type="text"
                                placeholder={`Value for ${varKey}`}
                                value={activeReminder.bodyVariables[varKey] || ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  setMessages(prev => prev.map(m => {
                                    if (m.id !== activeMessage.id) return m;
                                    return {
                                      ...m,
                                      reminders: (m.reminders || []).map(r => {
                                        if (r.id !== activeReminder.id) return r;
                                        return {
                                          ...r,
                                          bodyVariables: { ...(r.bodyVariables || {}), [varKey]: val }
                                        };
                                      })
                                    };
                                  }));
                                }}
                                style={{
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  padding: '8px 12px',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '6px',
                                  fontSize: '12.5px',
                                  outline: 'none'
                                }}
                              />
                            </div>
                          ))
                        )}
                      </div>

                      {/* Button Actions */}
                      <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>💬</span> Button Reply Follow-up Actions
                          </div>
                          <button
                            type="button"
                            onClick={handleActiveReminderAddButtonAction}
                            style={{
                              background: 'rgba(99, 102, 241, 0.1)',
                              color: '#6366f1',
                              border: 'none',
                              padding: '5px 12px',
                              borderRadius: '6px',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              cursor: 'pointer'
                            }}
                          >
                            + Add Button Action
                          </button>
                        </div>

                        {buttonKeys.length === 0 ? (
                          <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                            No interactive template buttons detected.
                          </p>
                        ) : (
                          buttonKeys.map(btnName => {
                            const actionConfig = activeReminder.buttonActions[btnName] || {};
                            return (
                              <div 
                                key={btnName}
                                style={{
                                  background: '#ffffff',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px',
                                  padding: '12px 14px',
                                  marginTop: '10px'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                                    Button Reply: "{btnName}"
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleActiveReminderRemoveButtonAction(btnName)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#ef4444',
                                      fontSize: '11.5px',
                                      fontWeight: 600,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                  <div>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                                      Action On Click
                                    </label>
                                    <select
                                      value={actionConfig.action || 'CONTINUE'}
                                      onChange={e => handleActiveReminderUpdateButtonAction(btnName, 'action', e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '7px 10px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        fontSize: '12.5px',
                                        background: '#ffffff',
                                        outline: 'none'
                                      }}
                                    >
                                      {BUTTON_ACTION_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                                      Analytics Category
                                    </label>
                                    <select
                                      value={actionConfig.category || 'INTERESTED'}
                                      onChange={e => handleActiveReminderUpdateButtonAction(btnName, 'category', e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '7px 10px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        fontSize: '12.5px',
                                        background: '#ffffff',
                                        outline: 'none'
                                      }}
                                    >
                                      {ANALYTICS_CATEGORIES.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                {actionConfig.action === 'SEND_TEMPLATE' && (
                                  <div style={{ marginTop: '10px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                                      Follow-up Template to Send
                                    </label>
                                    <select
                                      value={actionConfig.followUpTemplateId || ''}
                                      onChange={e => handleActiveReminderUpdateButtonAction(btnName, 'followUpTemplateId', e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '7px 10px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        fontSize: '12.5px',
                                        background: '#ffffff',
                                        outline: 'none'
                                      }}
                                    >
                                      <option value="">Select follow-up template...</option>
                                      {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : activeMessage ? (
              <div 
                style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                }}
              >
                {/* Inspector Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                      MESSAGE #{activeMessageIndex + 1} INSPECTOR
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', margin: '4px 0 0 0' }}>
                      {activeMessage.title || `Message ${activeMessageIndex + 1}`}
                    </h3>
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', background: '#f8fafc', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>
                    {activeMessage.timingType === 'IMMEDIATE' ? '⚡ Immediate' : '📅 Scheduled'}
                  </div>
                </div>

                {/* Field 1: Message Title / Label */}
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Message Name / Sequence Label *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Day 1 - Welcome & Zoom Link"
                    value={activeMessage.title || ''}
                    onChange={e => handleUpdateActiveField('title', e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '10px 14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '13.5px',
                      outline: 'none',
                      color: '#0f172a'
                    }}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = '#cbd5e1'}
                  />
                </div>

                {/* Field 2: Timing Configuration */}
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Delivery Timing
                  </label>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <select
                      value={activeMessage.timingType}
                      onChange={e => handleUpdateActiveField('timingType', e.target.value)}
                      style={{
                        padding: '9px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '13px',
                        background: '#ffffff',
                        outline: 'none',
                        color: '#334155',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="SCHEDULED">Send at date & time</option>
                      <option value="IMMEDIATE">Send immediately on launch</option>
                    </select>

                    {activeMessage.timingType === 'SCHEDULED' && (
                      <input
                        type="datetime-local"
                        value={activeMessage.scheduledAt || ''}
                        onChange={e => handleUpdateActiveField('scheduledAt', e.target.value)}
                        style={{
                          flex: 1,
                          minWidth: '220px',
                          padding: '9px 12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          fontSize: '13px',
                          outline: 'none',
                          color: '#334155'
                        }}
                      />
                    )}
                  </div>

                  {/* Quick Time Shift Chips */}
                  {activeMessage.timingType === 'SCHEDULED' && activeMessage.scheduledAt && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Quick Shift:</span>
                      {[
                        { label: '+30m', mins: 30 },
                        { label: '+1h', mins: 60 },
                        { label: '+1 Day', mins: 1440 },
                        { label: '+2 Days', mins: 2880 },
                        { label: '-1 Day', mins: -1440 },
                      ].map(shift => (
                        <button
                          key={shift.label}
                          type="button"
                          onClick={() => {
                            const shifted = addMinutesToDateTimeLocal(activeMessage.scheduledAt, shift.mins);
                            handleUpdateActiveField('scheduledAt', shifted);
                          }}
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            color: '#475569',
                            borderRadius: '5px',
                            padding: '2px 8px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          {shift.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Field 3: Message Template Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    WhatsApp Message Template *
                  </label>
                  <select
                    value={activeMessage.templateId || ''}
                    onChange={e => handleActiveTemplateSelect(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '13.5px',
                      color: '#0f172a',
                      background: '#ffffff',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select approved template...</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.category || 'MARKETING'})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Template Variables & Button Actions */}
                {(() => {
                  const selectedTpl = templates.find(t => String(t.id) === String(activeMessage.templateId));
                  if (!selectedTpl) return null;

                  const bodyPreview = extractTemplateBodyText(selectedTpl);
                  const bodyVarKeys = Object.keys(activeMessage.bodyVariables || {});
                  const buttonKeys = Object.keys(activeMessage.buttonActions || {});

                  return (
                    <div 
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #e0e7ff',
                        borderRadius: '12px',
                        padding: '18px 20px',
                        marginTop: '16px'
                      }}
                    >
                      {/* Header: Variables */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#4338ca', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>✏️</span> Variables for "{selectedTpl.name}"
                        </div>
                        <button
                          type="button"
                          onClick={handleActiveAddVariableField}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #cbd5e1',
                            color: '#4338ca',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          + Add Variable Field
                        </button>
                      </div>

                      {/* Body Preview */}
                      {bodyPreview && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#2563eb', letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '4px' }}>
                            TEMPLATE TEXT PREVIEW
                          </div>
                          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', fontSize: '12.5px', color: '#334155', lineHeight: 1.5, maxHeight: '110px', overflowY: 'auto' }}>
                            {bodyPreview}
                          </div>
                        </div>
                      )}

                      {/* Header Variable */}
                      <div style={{ marginBottom: '14px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Header Parameter Value:
                        </label>
                        <input
                          type="text"
                          placeholder="Value for Header Parameter (media URL or text)"
                          value={activeMessage.headerVariable || ''}
                          onChange={e => handleUpdateActiveField('headerVariable', e.target.value)}
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 12px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            fontSize: '12.5px',
                            background: '#ffffff',
                            outline: 'none'
                          }}
                        />
                      </div>

                      {/* Body Variables List */}
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                          Body Variables ({bodyVarKeys.length} parameters configured)
                        </div>

                        {bodyVarKeys.length === 0 ? (
                          <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', padding: '6px 0' }}>
                            No body variables found in this template. Click "+ Add Variable Field" to add one manually.
                          </div>
                        ) : (
                          bodyVarKeys.map((varKey, vIdx) => (
                            <div 
                              key={varKey}
                              style={{
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '12px 14px',
                                marginBottom: '10px'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#1e293b' }}>
                                    Variable {"{{"}{isNaN(varKey) ? vIdx + 1 : varKey}{"}}"} ({varKey})
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleActiveRemoveVariableField(varKey)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#ef4444',
                                      fontSize: '11px',
                                      fontWeight: 700,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    ✕ Remove
                                  </button>
                                </div>

                                {/* Shortcut Chips */}
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {[
                                    { label: '+ Contact Name', value: '{{name}}' },
                                    { label: '+ Event Title', value: campaignName || '{{event_title}}' },
                                    { label: '+ Event Date', value: '{{event_date}}' },
                                    { label: '+ Phone', value: '{{phone}}' },
                                  ].map(chip => (
                                    <button
                                      key={chip.label}
                                      type="button"
                                      onClick={() => handleActiveInsertShortcut(varKey, chip.value)}
                                      style={{
                                        background: '#f1f5f9',
                                        border: '1px solid #cbd5e1',
                                        color: '#2563eb',
                                        padding: '3px 8px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                      }}
                                    >
                                      {chip.label}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <input
                                type="text"
                                placeholder={`Value for ${varKey}`}
                                value={activeMessage.bodyVariables[varKey] || ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  setMessages(prev => {
                                    const updated = [...prev];
                                    const cur = { ...updated[activeMessageIndex] };
                                    cur.bodyVariables = { ...(cur.bodyVariables || {}), [varKey]: val };
                                    updated[activeMessageIndex] = cur;
                                    return updated;
                                  });
                                }}
                                style={{
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  padding: '8px 12px',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '6px',
                                  fontSize: '12.5px',
                                  outline: 'none'
                                }}
                              />
                            </div>
                          ))
                        )}
                      </div>

                      {/* Button Actions */}
                      <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>💬</span> Button Reply Follow-up Actions
                          </div>
                          <button
                            type="button"
                            onClick={handleActiveAddButtonAction}
                            style={{
                              background: 'rgba(99, 102, 241, 0.1)',
                              color: '#6366f1',
                              border: 'none',
                              padding: '5px 12px',
                              borderRadius: '6px',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              cursor: 'pointer'
                            }}
                          >
                            + Add Button Action
                          </button>
                        </div>

                        {buttonKeys.length === 0 ? (
                          <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                            No interactive template buttons detected. Select a template with buttons or click "+ Add Button Action" to configure follow-up responses.
                          </p>
                        ) : (
                          buttonKeys.map(btnName => {
                            const actionConfig = activeMessage.buttonActions[btnName] || {};
                            return (
                              <div 
                                key={btnName}
                                style={{
                                  background: '#ffffff',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px',
                                  padding: '12px 14px',
                                  marginTop: '10px'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                                    Button Reply: "{btnName}"
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleActiveRemoveButtonAction(btnName)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#ef4444',
                                      fontSize: '11.5px',
                                      fontWeight: 600,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                  <div>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                                      Action On Click
                                    </label>
                                    <select
                                      value={actionConfig.action || 'CONTINUE'}
                                      onChange={e => handleActiveUpdateButtonAction(btnName, 'action', e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '7px 10px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        fontSize: '12.5px',
                                        background: '#ffffff',
                                        outline: 'none'
                                      }}
                                    >
                                      {BUTTON_ACTION_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                                      Analytics Category
                                    </label>
                                    <select
                                      value={actionConfig.category || 'INTERESTED'}
                                      onChange={e => handleActiveUpdateButtonAction(btnName, 'category', e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '7px 10px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        fontSize: '12.5px',
                                        background: '#ffffff',
                                        outline: 'none'
                                      }}
                                    >
                                      {ANALYTICS_CATEGORIES.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                {actionConfig.action === 'SEND_TEMPLATE' && (
                                  <div style={{ marginTop: '10px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                                      Follow-up Template to Send
                                    </label>
                                    <select
                                      value={actionConfig.followUpTemplateId || ''}
                                      onChange={e => handleActiveUpdateButtonAction(btnName, 'followUpTemplateId', e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '7px 10px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        fontSize: '12.5px',
                                        background: '#ffffff',
                                        outline: 'none'
                                      }}
                                    >
                                      <option value="">Select follow-up template...</option>
                                      {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div style={{ background: '#ffffff', border: '1px dashed #cbd5e1', borderRadius: '14px', padding: '40px 24px', textAlign: 'center', color: '#64748b' }}>
                Select or add a message to edit its configuration
              </div>
            )}
          </div>

          {/* ── Bottom Section: What will be sent, in order ── */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '22px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', marginTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div>
                <h3 style={{ fontSize: '15.5px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  What will be sent, in order
                </h3>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '3px 0 0 0' }}>
                  Chronological delivery schedule for this campaign sequence ({chronologicalTimeline.length} steps)
                </p>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '11.5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <th style={{ padding: '10px 14px' }}>Step</th>
                    <th style={{ padding: '10px 14px' }}>Message / Reminder</th>
                    <th style={{ padding: '10px 14px' }}>Delivery Time</th>
                    <th style={{ padding: '10px 14px' }}>Template</th>
                    <th style={{ padding: '10px 14px' }}>Parameters</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {chronologicalTimeline.map((item, stepIdx) => {
                    const tpl = templates.find(t => String(t.id) === String(item.templateId));
                    const isCurrentActive = item.type === 'REMINDER'
                      ? (activeSelection.type === 'REMINDER' && activeSelection.reminderId === item.reminderId)
                      : (activeSelection.type === 'MESSAGE' && activeSelection.messageId === item.messageId);
                    const varCount = Object.keys(item.bodyVariables || {}).length;

                    return (
                      <tr 
                        key={item.id}
                        onClick={() => {
                          if (item.type === 'REMINDER') {
                            setActiveSelection({ type: 'REMINDER', messageId: item.messageId, reminderId: item.reminderId });
                          } else {
                            setActiveSelection({ type: 'MESSAGE', messageId: item.messageId, reminderId: null });
                          }
                        }}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          background: isCurrentActive ? '#f5f3ff' : 'transparent',
                          cursor: 'pointer',
                          transition: 'background 0.12s ease'
                        }}
                        onMouseEnter={e => { if (!isCurrentActive) e.currentTarget.style.background = '#fafafa'; }}
                        onMouseLeave={e => { if (!isCurrentActive) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td style={{ padding: '12px 14px', fontWeight: 800, color: isCurrentActive ? '#6366f1' : '#64748b' }}>
                          #{stepIdx + 1}
                        </td>
                        <td style={{ padding: '12px 14px', fontWeight: 700, color: item.isReminder ? '#3730a3' : '#0f172a' }}>
                          {item.title}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#334155' }}>
                          {item.timingType === 'IMMEDIATE' ? (
                            <span style={{ color: '#16a34a', fontWeight: 700, background: '#dcfce7', padding: '3px 8px', borderRadius: '4px', fontSize: '11.5px' }}>
                              ⚡ Immediate on launch
                            </span>
                          ) : (
                            <span style={{ fontFamily: 'monospace', fontSize: '12.5px', fontWeight: 600 }}>
                              {item.scheduledAt ? formatDateTimeDisplay(item.scheduledAt) : (item.isReminder ? `+${item.delayMins}m after parent` : 'Unscheduled')}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {tpl ? (
                            <span style={{ color: '#0f172a', fontWeight: 600 }}>{tpl.name}</span>
                          ) : (
                            <span style={{ color: '#dc2626', fontWeight: 600, fontSize: '12px' }}>⚠️ Unassigned</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#64748b', fontSize: '12px' }}>
                          {varCount} variable{varCount === 1 ? '' : 's'}
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.type === 'REMINDER') {
                                setActiveSelection({ type: 'REMINDER', messageId: item.messageId, reminderId: item.reminderId });
                              } else {
                                setActiveSelection({ type: 'MESSAGE', messageId: item.messageId, reminderId: null });
                              }
                            }}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              background: isCurrentActive ? '#6366f1' : '#ffffff',
                              color: isCurrentActive ? '#ffffff' : '#475569',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              cursor: 'pointer'
                            }}
                          >
                            {isCurrentActive ? 'Editing' : 'Edit'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sticky / Fixed Footer for Step 2 */}
          <div 
            style={{
              position: 'sticky',
              bottom: '16px',
              background: '#ffffff',
              padding: '16px 24px',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 100,
              marginTop: '16px'
            }}
          >
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Configured <strong>{messages.length}</strong> message{messages.length > 1 ? 's' : ''} for <strong>{finalWillReceiveCount}</strong> recipients
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  setCurrentStep(1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '13.5px',
                  cursor: 'pointer'
                }}
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSaveMessagesAndContinue}
                style={{
                  background: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '13.5px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Save Messages & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* STEP 3: REVIEW & FINISH                                      */}
      {/* ───────────────────────────────────────────────────────────── */}
      {currentStep === 3 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Stat Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                AUDIENCE
              </div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>
                {finalWillReceiveCount} contacts
              </div>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                MESSAGES
              </div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>
                {messages.length}
              </div>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                STATUS
              </div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: isActive ? '#10b981' : '#f59e0b', marginTop: '4px' }}>
                {isActive ? 'Active' : 'Inactive'}
              </div>
            </div>
          </div>

          {/* Message Schedule Summary Card */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '22px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: '0 0 16px 0' }}>
              Message schedule
            </h2>

            {messages.length === 0 ? (
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                No messages yet. Go back to step 2 to add some.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {messages.map((m, idx) => {
                  const tpl = templates.find(t => String(t.id) === String(m.templateId));
                  return (
                    <div 
                      key={m.id}
                      style={{
                        padding: '14px 18px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '10px'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                          {m.title || `Message ${idx + 1}`}: {tpl ? tpl.name : 'No template selected'}
                        </div>
                        <div style={{ fontSize: '12.5px', color: '#64748b', marginTop: '2px' }}>
                          Timing: {m.timingType === 'IMMEDIATE' ? '⚡ Immediate on launch' : `📅 ${formatDateTimeDisplay(m.scheduledAt)}`} • {Object.keys(m.bodyVariables || {}).length} variables • {Object.keys(m.buttonActions || {}).length} button actions
                        </div>
                        {m.reminders && m.reminders.length > 0 && (
                          <div style={{ marginTop: '8px', paddingLeft: '12px', borderLeft: '2px solid #c7d2fe', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {m.reminders.map((rem, rIdx) => {
                              const remTpl = templates.find(t => String(t.id) === String(rem.templateId));
                              return (
                                <div key={rem.id || rIdx} style={{ fontSize: '12px', color: '#4338ca', fontWeight: 600 }}>
                                  ↳ Reminder: +{rem.delayValue} {rem.delayUnit} · {remTpl ? remTpl.name : '⚠️ No template'} · {rem.skipIfReplied !== false ? 'if no reply' : 'always'}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveMessageId(m.id);
                          setCurrentStep(2);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          background: '#ffffff',
                          color: '#4338ca',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Edit in Step 2 →
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sticky / Fixed Footer for Step 3 */}
          <div 
            style={{
              position: 'sticky',
              bottom: '16px',
              background: '#ffffff',
              padding: '16px 24px',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 100,
              marginTop: '16px',
              flexWrap: 'wrap',
              gap: '12px'
            }}
          >
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer' }}
              />
              Active: messages send at their scheduled times
            </label>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  setCurrentStep(1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  padding: '9px 16px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Edit setup
              </button>
              <button
                type="button"
                onClick={() => {
                  setCurrentStep(2);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  padding: '9px 16px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Edit messages
              </button>
              <button
                type="button"
                onClick={() => {
                  const currentHost = window.location.origin;
                  const cleanName = encodeURIComponent(campaignName || 'campaign');
                  const publicUrl = `${currentHost}/whatsapp/public-campaign/marketing/${cleanName}`;
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(publicUrl);
                    showSuccess('Public campaign link copied to clipboard!');
                  } else {
                    prompt('Copy public campaign URL:', publicUrl);
                  }
                }}
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  padding: '9px 16px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span>🔗</span> Copy public link
              </button>
              <button
                type="button"
                onClick={handleFinishCampaign}
                disabled={isFinishing}
                style={{
                  background: '#6366f1',
                  color: '#ffffff',
                  border: 'none',
                  padding: '9px 24px',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: isFinishing ? 'not-allowed' : 'pointer',
                  opacity: isFinishing ? 0.7 : 1,
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)'
                }}
              >
                {isFinishing ? 'Saving...' : 'Finish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCampaign;

// ============================================================================
// PREVIOUS ORIGINAL CREATE CAMPAIGN CODE (PRESERVED FOR FEATURE REFERENCE)
// ============================================================================
// import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
// 
// import { createPortal } from 'react-dom';
// 
// import { getContacts, bulkUploadContacts, createContact } from '../../api-wa/contactsApi';
// 
// import { getTemplates, sendCampaign, createTemplate } from '../../api-wa/templatesApi';
// 
// import { getCampaign, createCampaign, updateCampaign, launchCampaign, createDraftCampaign, launchCampaignNew } from '../../api-wa/campaignsApi';
// 
// import { getApiErrorMessage } from '../../api-wa/client';
// 
// import AddContactModal from '../../Components/WhatsApp/AddContactModal';
// 
// import { ToastContext } from '../../Context/WaToastContext';
// 
// 
// 
// const getRenderedPreview = (body, variables) => {
// 
//   if (!body) return '';
// 
//   let rendered = body;
// 
//   Object.keys(variables).forEach(v => {
// 
//     // Only process numerical variables like {{1}}, {{2}}
// 
//     if (isNaN(v)) return;
// 
// 
// 
//     const val = variables[v];
// 
//     const placeholder = `{{${v}}}`;
// 
//     const replacement = val && typeof val === 'string' && val.trim() ? val : placeholder;
// 
//     rendered = rendered.split(placeholder).join(replacement);
// 
//   });
// 
//   return rendered;
// 
// };
// 
// 
// 
// // WhatsApp text formatting: *bold*, _italic_, ~strikethrough~
// 
// const formatWhatsAppText = (text) => {
// 
//   if (!text) return '';
// 
//   // Split into parts, preserving formatting markers
// 
//   const parts = [];
// 
//   let remaining = text;
// 
//   const regex = /(\*[^\s*\n](?:[^*]*?[^\s*\n])?\*|_[^\s_\n](?:[^_]*?[^\s_\n])?_|~[^\s~\n](?:[^~]*?[^\s~\n])?~)/g;
// 
//   let lastIndex = 0;
// 
//   let match;
// 
//   while ((match = regex.exec(remaining)) !== null) {
// 
//     if (match.index > lastIndex) {
// 
//       parts.push(remaining.substring(lastIndex, match.index));
// 
//     }
// 
//     const raw = match[0];
// 
//     if (raw.startsWith('*') && raw.endsWith('*')) {
// 
//       parts.push(<strong key={match.index}>{raw.slice(1, -1)}</strong>);
// 
//     } else if (raw.startsWith('_') && raw.endsWith('_')) {
// 
//       parts.push(<em key={match.index}>{raw.slice(1, -1)}</em>);
// 
//     } else if (raw.startsWith('~') && raw.endsWith('~')) {
// 
//       parts.push(<s key={match.index}>{raw.slice(1, -1)}</s>);
// 
//     }
// 
//     lastIndex = match.index + raw.length;
// 
//   }
// 
//   if (lastIndex < remaining.length) {
// 
//     parts.push(remaining.substring(lastIndex));
// 
//   }
// 
//   return parts.length > 0 ? parts : text;
// 
// };
// 
// 
// 
// const CreateCampaign = ({ onBack, onSaved, onCreateTemplate, setIsDirty, closeAttempted, onCancelClose, onConfirmClose }) => {
// 
//   const { showError } = useContext(ToastContext);
// 
//   const [dbContacts, setDbContacts] = useState([]);
// 
//   const [customContacts, setCustomContacts] = useState([]); // {id, name, phone_number, source}
// 
//   const [loading, setLoading] = useState(true);
// 
//   const [sending, setSending] = useState(false);
// 
//   const [error, setError] = useState(null);
// 
//   const [sendStatus, setSendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'failed'
// 
//   const [sendError, setSendError] = useState(null);
// 
// 
// 
//   const [campaignName, setCampaignName] = useState('');
// 
//   const [selectedContactIds, setSelectedContactIds] = useState([]);
// 
//   const [searchTerm, setSearchTerm] = useState('');
// 
//   const [showScheduleModal, setShowScheduleModal] = useState(false);
// 
//   const [scheduleTime, setScheduleTime] = useState('');
// 
//   const [showAddModal, setShowAddModal] = useState(false);
// 
// 
// 
//   // Draft editing state
// 
//   const [editCampaignDraftId, setEditCampaignDraftId] = useState(null);
// 
//   const [savingDraft, setSavingDraft] = useState(false);
// 
//   const [isSavingDraftStatus, setIsSavingDraftStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
// 
//   const [originalStatus, setOriginalStatus] = useState('');
// 
// 
// 
// 
// 
//   // Template state
// 
//   const [templates, setTemplates] = useState([]);
// 
//   const [selectedTemplateId, setSelectedTemplateId] = useState('');
// 
//   const [templateVariables, setTemplateVariables] = useState({}); // { '1': '', '2': '' }
// 
//   const [templateLoading, setTemplateLoading] = useState(false);
// 
//   const [headerMediaUrl, setHeaderMediaUrl] = useState('');
// 
//   const [headerMediaFile, setHeaderMediaFile] = useState(null);
// 
// 
// 
//   // Template Modal & Draft state
// 
//   const [showTemplateModal, setShowTemplateModal] = useState(false);
// 
//   const [templateData, setTemplateData] = useState({ name: '', body: '', category: 'MARKETING' });
// 
//   const [isDrafting, setIsDrafting] = useState(false);
// 
//   const [showDraftBanner, setShowDraftBanner] = useState(false);
// 
//   const [successMessage, setSuccessMessage] = useState(null);
// 
// 
// 
//   // Paste Panel state
// 
//   const [pasteMode, setPasteMode] = useState(false);
// 
//   const [pastedText, setPastedText] = useState('');
// 
//   const [validationResult, setValidationResult] = useState({ valid: [], invalid: [] });
// 
// 
// 
//   const fileInputRef = useRef(null);
// 
//   const isLoadingDraftRef = useRef(false);
// 
//   // React state (`sending`/`sendStatus`) only disables the button after a re-render, which is too
// 
//   // late for a fast double-click/double-tap - both clicks land before the first setSending(true)
// 
//   // is reflected in the DOM, firing two campaign-create requests. This ref is checked synchronously.
// 
//   const isSubmittingRef = useRef(false);
// 
//   const [uploadType, setUploadType] = useState(null); // 'excel' or 'csv'
// 
//   const [showBulkPanel, setShowBulkPanel] = useState(false);
// 
//   const [bulkPanelTab, setBulkPanelTab] = useState('excel'); // 'excel' | 'csv' | 'paste'
// 
// 
// 
//   const [hasSaved, setHasSaved] = useState(false);
// 
//   const isDirty = useMemo(() => {
// 
//     if (hasSaved) return false;
// 
//     const nameEntered = campaignName.trim() !== '';
// 
//     const templateSelected = selectedTemplateId !== '';
// 
//     const contactsSelected = selectedContactIds.length > 0;
// 
//     const scheduleSet = scheduleTime !== '';
// 
//     const varsEntered = Object.values(templateVariables).some(v => v !== '');
// 
//     return nameEntered || templateSelected || contactsSelected || scheduleSet || varsEntered;
// 
//   }, [campaignName, selectedTemplateId, selectedContactIds, scheduleTime, templateVariables, hasSaved]);
// 
// 
// 
//   useEffect(() => {
// 
//     if (setIsDirty) {
// 
//       setIsDirty(isDirty);
// 
//     }
// 
//     return () => {
// 
//       if (setIsDirty) {
// 
//         setIsDirty(false);
// 
//       }
// 
//     };
// 
//   }, [isDirty, setIsDirty]);
// 
// 
// 
//   // Load draft on mount
// 
//   useEffect(() => {
// 
//     const savedDraft = localStorage.getItem('campaign_template_draft');
// 
//     if (savedDraft) {
// 
//       setTemplateData(JSON.parse(savedDraft));
// 
//       setShowDraftBanner(true);
// 
//     }
// 
// 
// 
//     const draftId = localStorage.getItem('editCampaignDraftId');
// 
//     if (draftId) {
// 
//       setEditCampaignDraftId(draftId);
// 
//       loadCampaignDraft(draftId);
// 
//     } else {
// 
//       fetchData();
// 
//     }
// 
//   }, []);
// 
// 
// 
//   useEffect(() => {
// 
//     if (isLoadingDraftRef.current) {
// 
//       return;
// 
//     }
// 
//     let autoMediaUrl = '';
// 
//     if (selectedTemplateId) {
// 
//       const template = templates.find(t => t.id.toString() === selectedTemplateId.toString());
// 
//       if (template) {
// 
//         if (template.body) {
// 
//           const matches = template.body.match(/\{\{(\d+)\}\}/g) || [];
// 
//           const vars = {};
// 
//           matches.forEach(m => {
// 
//             const num = m.replace(/\{\{|\}\}/g, '');
// 
//             vars[num] = '';
// 
//           });
// 
//           setTemplateVariables(prev => {
// 
//             const updated = { ...vars };
// 
//             Object.keys(updated).forEach(k => {
// 
//               if (prev[k] !== undefined && prev[k] !== '') {
// 
//                 updated[k] = prev[k];
// 
//               }
// 
//             });
// 
//             return updated;
// 
//           });
// 
//         } else {
// 
//           setTemplateVariables({});
// 
//         }
// 
// 
// 
//         autoMediaUrl = 
// 
//           template.uploaded_preview_image ||
// 
//           template.meta_media_url ||
// 
//           template.header_media ||
// 
//           (template.sample_values && (template.sample_values.header_handle || template.sample_values.media_url)) ||
// 
//           '';
// 
//       }
// 
//     } else {
// 
//       setTemplateVariables({});
// 
//     }
// 
//     setHeaderMediaUrl(autoMediaUrl);
// 
//     setHeaderMediaFile(null);
// 
//   }, [selectedTemplateId, templates]);
// 
// 
// 
//   // Auto-save draft every 2 seconds
// 
//   useEffect(() => {
// 
//     let interval;
// 
//     if (showTemplateModal && (templateData.name || templateData.body)) {
// 
//       interval = setInterval(() => {
// 
//         localStorage.setItem('campaign_template_draft', JSON.stringify(templateData));
// 
//         setIsDrafting(true);
// 
//         setTimeout(() => setIsDrafting(false), 800);
// 
//       }, 2000);
// 
//     }
// 
//     return () => clearInterval(interval);
// 
//   }, [showTemplateModal, templateData]);
// 
// 
// 
//   // Build campaign payload
// 
//   const buildCampaignPayload = (isDraft = false, dateToSchedule = null, overrideContactIds = null) => {
// 
//     const nameToSave = campaignName.trim() || `Draft Campaign - ${new Date().toLocaleDateString()}`;
// 
//     const dbContactIds = overrideContactIds || selectedContactIds.filter(id => !String(id).startsWith('custom-'));
// 
//     const customContactsToSave = customContacts.filter(l => selectedContactIds.includes(l.id));
// 
// 
// 
//     const payloadVariables = { ...templateVariables };
// 
//     if (isDraft && customContactsToSave.length > 0) {
// 
//       payloadVariables['_custom_contacts'] = customContactsToSave;
// 
//     }
// 
// 
// 
//     const payload = {
// 
//       name: nameToSave,
// 
//       template_id: selectedTemplateId ? Number(selectedTemplateId) : null,
// 
//       contact_ids: dbContactIds,
// 
//       variables: payloadVariables
// 
//     };
// 
//     if (headerMediaUrl) {
// 
//       payload.variables['__header_media_url__'] = headerMediaUrl;
// 
//     }
// 
//     if (dateToSchedule) payload.scheduled_at = dateToSchedule;
// 
//     return payload;
// 
//   };
// 
// 
// 
//   // Background Auto-Save Effect disabled
// 
// 
// 
//   const loadCampaignDraft = async (draftId) => {
// 
//     try {
// 
//       setLoading(true);
// 
//       setError(null);
// 
//       isLoadingDraftRef.current = true;
// 
// 
// 
//       // Fetch templates and contacts first. page_size is explicit - without it the default
// 
//       // page cuts off well short of the full list, so matching a large campaign's contacts
// 
//       // against it silently drops most of them (backend caps at 10000, ContactPagination).
// 
//       const contactsData = await getContacts({ page_size: 10000 });
// 
//       const loadedContacts = Array.isArray(contactsData?.results) ? contactsData.results : (Array.isArray(contactsData) ? contactsData : []);
// 
//       setDbContacts(loadedContacts);
// 
// 
// 
//       setTemplateLoading(true);
// 
//       const templatesData = await getTemplates();
// 
//       setTemplates(templatesData);
// 
//       setTemplateLoading(false);
// 
// 
// 
//       // Fetch campaign draft
// 
//       const campaign = await getCampaign(draftId);
// 
//       if (campaign) {
// 
//         setOriginalStatus(campaign.status || '');
// 
//         setCampaignName(campaign.name || '');
// 
//         if (campaign.template) {
// 
//           // Handle both ID or object nested template
// 
//           const tId = typeof campaign.template === 'object' ? campaign.template.id : campaign.template;
// 
//           setSelectedTemplateId(tId?.toString() || '');
// 
//         }
// 
// 
// 
//         let loadedCustomContacts = [];
// 
//         if (campaign.variables) {
// 
//           const { _custom_contacts, __header_media_url__, ...cleanVars } = campaign.variables;
// 
// 
// 
//           setTemplateVariables(cleanVars);
// 
// 
// 
//           if (__header_media_url__) {
// 
//             setHeaderMediaUrl(__header_media_url__);
// 
//           }
// 
// 
// 
//           if (Array.isArray(_custom_contacts)) {
// 
//             loadedCustomContacts = _custom_contacts;
// 
//             setCustomContacts(_custom_contacts);
// 
//           }
// 
//         } else {
// 
//           setTemplateVariables({});
// 
//         }
// 
// 
// 
//         const dbPhones = Array.isArray(campaign.contacts) ? campaign.contacts : [];
// 
//         const matchedDbIds = dbPhones.map(phone => {
// 
//           const match = loadedContacts.find(c => {
// 
//             const cPhone = c.phone_number?.replace(/\D/g, '') || '';
// 
//             const queryPhone = phone.replace(/\D/g, '') || '';
// 
//             return cPhone.endsWith(queryPhone) || queryPhone.endsWith(cPhone);
// 
//           });
// 
//           return match ? match.id : null;
// 
//         }).filter(Boolean);
// 
//         const customIds = loadedCustomContacts.map(l => l.id);
// 
//         setSelectedContactIds(Array.from(new Set([...matchedDbIds, ...customIds])));
// 
// 
// 
//         if (campaign.scheduled_at) {
// 
//           try {
// 
//             setScheduleTime(campaign.scheduled_at.slice(0, 16));
// 
//           } catch (e) {
// 
//             console.error("Failed to parse scheduled_at:", e);
// 
//           }
// 
//         }
// 
//       }
// 
//     } catch (err) {
// 
//       setError(getApiErrorMessage(err, 'Failed to load campaign draft'));
// 
//     } finally {
// 
//       setLoading(false);
// 
//       setTemplateLoading(false);
// 
//       setTimeout(() => {
// 
//         isLoadingDraftRef.current = false;
// 
//       }, 500);
// 
//     }
// 
//   };
// 
// 
// 
//   const fetchData = async () => {
// 
//     try {
// 
//       setLoading(true);
// 
//       const contactsData = await getContacts({ page_size: 10000 });
// 
//       setDbContacts(Array.isArray(contactsData?.results) ? contactsData.results : (Array.isArray(contactsData) ? contactsData : []));
// 
// 
// 
//       setTemplateLoading(true);
// 
//       // Fetch all templates to show in the grid
// 
//       const templatesData = await getTemplates();
// 
//       setTemplates(templatesData);
// 
//     } catch (err) {
// 
//       setError(getApiErrorMessage(err, 'Failed to fetch data'));
// 
//     } finally {
// 
//       setLoading(false);
// 
//       setTemplateLoading(false);
// 
//     }
// 
//   };
// 
// 
// 
//   // Combine DB contacts and custom contacts
// 
//   const allContacts = useMemo(() => {
// 
//     return [...dbContacts, ...customContacts];
// 
//   }, [dbContacts, customContacts]);
// 
// 
// 
//   const filteredContacts = useMemo(() => {
// 
//     const term = searchTerm.toLowerCase().trim();
// 
//     if (!term) return allContacts;
// 
//     return allContacts.filter(l =>
// 
//       (l.name && l.name.toLowerCase().includes(term)) ||
// 
//       (l.phone_number && l.phone_number.includes(term))
// 
//     );
// 
//   }, [allContacts, searchTerm]);
// 
// 
// 
//   const { leftContacts, rightContacts } = useMemo(() => {
// 
//     const half = Math.ceil(filteredContacts.length / 2);
// 
//     return {
// 
//       leftContacts: filteredContacts.slice(0, half),
// 
//       rightContacts: filteredContacts.slice(half)
// 
//     };
// 
//   }, [filteredContacts]);
// 
// 
// 
//   const selectedTemplate = useMemo(() => {
// 
//     if (!selectedTemplateId || templates.length === 0) return null;
// 
//     return templates.find(t => t.id.toString() === selectedTemplateId.toString()) || null;
// 
//   }, [templates, selectedTemplateId]);
// 
// 
// 
//   // Contact toggling
// 
//   const handleToggleContact = (id) => {
// 
//     setSelectedContactIds(prev =>
// 
//       prev.includes(id) ? prev.filter(lid => lid !== id) : [...prev, id]
// 
//     );
// 
//   };
// 
// 
// 
//   const handleSelectAll = () => {
// 
//     if (selectedContactIds.length === filteredContacts.length) {
// 
//       setSelectedContactIds([]);
// 
//     } else {
// 
//       setSelectedContactIds(filteredContacts.map(l => l.id));
// 
//     }
// 
//   };
// 
// 
// 
//   // --- Template Management ---
// 
//   const handleOpenTemplateModal = () => {
// 
//     if (onCreateTemplate) {
// 
//       onCreateTemplate();
// 
//       return;
// 
//     }
// 
//     setShowTemplateModal(true);
// 
//     setShowDraftBanner(false);
// 
//   };
// 
// 
// 
//   const handleCloseTemplateModal = () => {
// 
//     setShowTemplateModal(false);
// 
//     if (templateData.name || templateData.body) {
// 
//       setShowDraftBanner(true);
// 
//     }
// 
//   };
// 
// 
// 
//   const handleSubmitTemplate = async (e) => {
// 
//     e.preventDefault();
// 
//     if (!templateData.name || !templateData.body) return alert('Please fill all fields');
// 
// 
// 
//     try {
// 
//       setTemplateLoading(true);
// 
//       await createTemplate(templateData);
// 
//       setSuccessMessage('Template submitted for review!');
// 
//       localStorage.removeItem('campaign_template_draft');
// 
//       setTemplateData({ name: '', body: '', category: 'MARKETING' });
// 
//       setShowTemplateModal(false);
// 
//       setShowDraftBanner(false);
// 
//       fetchData(); // Refresh list
// 
//       setTimeout(() => setSuccessMessage(null), 5000);
// 
//     } catch (err) {
// 
//       alert(getApiErrorMessage(err, 'Failed to create template'));
// 
//     } finally {
// 
//       setTemplateLoading(false);
// 
//     }
// 
//   };
// 
// 
// 
// 
// 
//   // --- Contact Upload Parsing ---
// 
//   const validatePhone = (num) => {
// 
//     const clean = num.toString().replace(/\D/g, '');
// 
//     return clean.length === 10 ? clean : null;
// 
//   };
// 
// 
// 
//   const processNumbers = (numbers, source) => {
// 
//     const valid = [];
// 
//     const invalid = [];
// 
// 
// 
//     numbers.forEach(num => {
// 
//       const cleaned = validatePhone(num);
// 
//       if (cleaned) valid.push(cleaned);
// 
//       else invalid.push(num);
// 
//     });
// 
// 
// 
//     const newContacts = valid.map(num => ({
// 
//       id: `custom-${source}-${num}-${Date.now()}-${Math.random()}`,
// 
//       name: `${num}`,
// 
//       phone_number: num,
// 
//       source: source.toUpperCase()
// 
//     }));
// 
// 
// 
//     setCustomContacts(prev => [...prev, ...newContacts]);
// 
//     setSelectedContactIds(prev => [...prev, ...newContacts.map(l => l.id)]);
// 
// 
// 
//     return { validCount: valid.length, invalidCount: invalid.length };
// 
//   };
// 
// 
// 
//   const handleFileUpload = (e) => {
// 
//     const file = e.target.files[0];
// 
//     if (!file) return;
// 
// 
// 
//     const reader = new FileReader();
// 
//     reader.onload = async (evt) => {
// 
//       if (uploadType === 'excel') {
// 
//         const mod = await import('xlsx');
// 
//         const XLSX = mod && (mod.default ?? mod);
// 
//         const wb = XLSX.read(evt.target.result, { type: 'binary' });
// 
//         const ws = wb.Sheets[wb.SheetNames[0]];
// 
//         const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
// 
//         const flat = data.flat().filter(cell => cell !== null && cell !== undefined);
// 
//         const result = processNumbers(flat, 'Excel');
// 
//         alert(`Imported ${result.validCount} valid numbers from Excel. ${result.invalidCount} were invalid.`);
// 
//       } else {
// 
//         const mod = await import('papaparse');
// 
//         const Papa = mod && (mod.default ?? mod);
// 
//         Papa.parse(file, {
// 
//           complete: (results) => {
// 
//             const flat = results.data.flat().filter(cell => cell);
// 
//             const result = processNumbers(flat, 'CSV');
// 
//             alert(`Imported ${result.validCount} valid numbers from CSV. ${result.invalidCount} were invalid.`);
// 
//           }
// 
//         });
// 
//       }
// 
//     };
// 
// 
// 
//     if (uploadType === 'excel') reader.readAsBinaryString(file);
// 
//     else reader.readAsText(file);
// 
// 
// 
//     e.target.value = null; // Reset input
// 
//   };
// 
// 
// 
//   // --- Paste Panel Logic ---
// 
//   const handleValidatePaste = () => {
// 
//     const lines = pastedText.split(/[\n,]+/).map(l => l.trim()).filter(l => l);
// 
//     const valid = [];
// 
//     const invalid = [];
// 
//     lines.forEach(line => {
// 
//       const cleaned = validatePhone(line);
// 
//       if (cleaned) valid.push(cleaned);
// 
//       else invalid.push(line);
// 
//     });
// 
//     setValidationResult({ valid, invalid });
// 
//   };
// 
// 
// 
//   const handleAddPasted = () => {
// 
//     processNumbers(validationResult.valid, 'Pasted');
// 
//     setPastedText('');
// 
//     setValidationResult({ valid: [], invalid: [] });
// 
//     setPasteMode(false);
// 
//   };
// 
// 
// 
//   const handleSaveDraft = async () => {
// 
//     if (!campaignName.trim()) {
// 
//       showError('Please enter a campaign name');
// 
//       return;
// 
//     }
// 
//     if (isSubmittingRef.current) return;
// 
//     isSubmittingRef.current = true;
// 
// 
// 
//     try {
// 
//       setSending(true);
// 
//       setError(null);
// 
// 
// 
//       const contactsPhones = selectedContactIds.map(id => {
// 
//         const contact = allContacts.find(c => c.id === id);
// 
//         return contact ? (contact.phone || contact.phone_number) : null;
// 
//       }).filter(Boolean);
// 
// 
// 
//       const payload = {
// 
//         id: editCampaignDraftId ? Number(editCampaignDraftId) : null,
// 
//         name: campaignName.trim(),
// 
//         template_id: selectedTemplateId ? Number(selectedTemplateId) : null,
// 
//         contacts: contactsPhones,
// 
//         variables: templateVariables,
// 
//         scheduled_at: scheduleTime ? new Date(scheduleTime).toISOString() : null,
// 
//         status: 'DRAFT'
// 
//       };
// 
// 
// 
//       await createDraftCampaign(payload);
// 
//       setHasSaved(true);
// 
//       localStorage.removeItem('editCampaignDraftId');
// 
// 
// 
//       if (onSaved) onSaved();
// 
//     } catch (err) {
// 
//       showError(getApiErrorMessage(err, 'Failed to save draft'));
// 
//     } finally {
// 
//       setSending(false);
// 
//       isSubmittingRef.current = false;
// 
//     }
// 
//   };
// 
// 
// 
//   const handleModalSaveDraft = async () => {
// 
//     if (!campaignName.trim()) {
// 
//       showError('Please enter a campaign name');
// 
//       return;
// 
//     }
// 
//     if (isSubmittingRef.current) return;
// 
//     isSubmittingRef.current = true;
// 
// 
// 
//     try {
// 
//       setSending(true);
// 
// 
// 
//       const contactsPhones = selectedContactIds.map(id => {
// 
//         const contact = allContacts.find(c => c.id === id);
// 
//         return contact ? (contact.phone || contact.phone_number) : null;
// 
//       }).filter(Boolean);
// 
// 
// 
//       const payload = {
// 
//         id: editCampaignDraftId ? Number(editCampaignDraftId) : null,
// 
//         name: campaignName.trim(),
// 
//         template_id: selectedTemplateId ? Number(selectedTemplateId) : null,
// 
//         contacts: contactsPhones,
// 
//         variables: templateVariables,
// 
//         scheduled_at: scheduleTime ? new Date(scheduleTime).toISOString() : null,
// 
//         status: 'DRAFT'
// 
//       };
// 
// 
// 
//       await createDraftCampaign(payload);
// 
//       setHasSaved(true);
// 
//       localStorage.removeItem('editCampaignDraftId');
// 
// 
// 
//       if (onConfirmClose) onConfirmClose();
// 
//     } catch (err) {
// 
//       showError(getApiErrorMessage(err, 'Failed to save draft'));
// 
//     } finally {
// 
//       setSending(false);
// 
//       isSubmittingRef.current = false;
// 
//     }
// 
//   };
// 
// 
// 
//   const handleModalSaveScheduled = async () => {
// 
//     if (!campaignName.trim()) {
// 
//       showError('Please enter a campaign name');
// 
//       return;
// 
//     }
// 
//     if (selectedContactIds.length === 0) {
// 
//       showError('Please select at least one contact');
// 
//       return;
// 
//     }
// 
//     if (!selectedTemplateId) {
// 
//       showError('Please select a template');
// 
//       return;
// 
//     }
// 
//     if (isSubmittingRef.current) return;
// 
//     isSubmittingRef.current = true;
// 
// 
// 
//     try {
// 
//       setSending(true);
// 
// 
// 
//       const contactsPhones = selectedContactIds.map(id => {
// 
//         const contact = allContacts.find(c => c.id === id);
// 
//         return contact ? (contact.phone || contact.phone_number) : null;
// 
//       }).filter(Boolean);
// 
// 
// 
//       const payloadVariables = { ...templateVariables };
// 
//       if (headerMediaUrl) {
// 
//         payloadVariables['__header_media_url__'] = headerMediaUrl;
// 
//       }
// 
// 
// 
//       const payload = {
// 
//         id: editCampaignDraftId ? Number(editCampaignDraftId) : null,
// 
//         name: campaignName.trim(),
// 
//         template_id: selectedTemplateId ? Number(selectedTemplateId) : null,
// 
//         contacts: contactsPhones,
// 
//         variables: payloadVariables,
// 
//         scheduled_at: scheduleTime ? new Date(scheduleTime).toISOString() : null
// 
//       };
// 
// 
// 
//       await launchCampaignNew(payload);
// 
//       setHasSaved(true);
// 
//       localStorage.removeItem('editCampaignDraftId');
// 
// 
// 
//       if (onConfirmClose) onConfirmClose();
// 
//     } catch (err) {
// 
//       showError(getApiErrorMessage(err, 'Failed to save scheduled campaign'));
// 
//     } finally {
// 
//       setSending(false);
// 
//       isSubmittingRef.current = false;
// 
//     }
// 
//   };
// 
// 
// 
// 
// 
//   const handleModalDiscard = () => {
// 
//     setHasSaved(true);
// 
//     localStorage.removeItem('editCampaignDraftId');
// 
//     if (onConfirmClose) onConfirmClose();
// 
//   };
// 
// 
// 
//   const handleSendCampaign = async () => {
// 
//     if (!campaignName.trim()) {
// 
//       setSendError('Please enter a campaign name');
// 
//       return;
// 
//     }
// 
//     if (selectedContactIds.length === 0) {
// 
//       setSendError('Please select at least one contact');
// 
//       return;
// 
//     }
// 
//     if (!selectedTemplateId) {
// 
//       setSendError('Please select a template');
// 
//       return;
// 
//     }
// 
//     if (isSubmittingRef.current) return;
// 
//     isSubmittingRef.current = true;
// 
// 
// 
//     try {
// 
//       setSendStatus('sending');
// 
//       setSendError(null);
// 
// 
// 
//       const contactsPhones = selectedContactIds.map(id => {
// 
//         const contact = allContacts.find(c => c.id === id);
// 
//         return contact ? (contact.phone || contact.phone_number) : null;
// 
//       }).filter(Boolean);
// 
// 
// 
//       const payloadVariables = { ...templateVariables };
// 
//       if (headerMediaUrl) {
// 
//         payloadVariables['__header_media_url__'] = headerMediaUrl;
// 
//       }
// 
// 
// 
//       const payload = {
// 
//         id: editCampaignDraftId ? Number(editCampaignDraftId) : null,
// 
//         name: campaignName.trim(),
// 
//         template_id: selectedTemplateId ? Number(selectedTemplateId) : null,
// 
//         contacts: contactsPhones,
// 
//         variables: payloadVariables,
// 
//         scheduled_at: scheduleTime ? new Date(scheduleTime).toISOString() : null
// 
//       };
// 
// 
// 
//       const result = await launchCampaignNew(payload);
// 
// 
// 
//       setSendStatus('sent');
// 
//       setSuccessMessage(
// 
//         originalStatus === 'SCHEDULED'
// 
//           ? `Scheduled campaign updated successfully!`
// 
//           : (scheduleTime
// 
//               ? `Campaign scheduled successfully for ${new Date(scheduleTime).toLocaleString()}!`
// 
//               : `Campaign launched successfully!`)
// 
//       );
// 
// 
// 
//       setHasSaved(true);
// 
//       localStorage.removeItem('editCampaignDraftId');
// 
// 
// 
//       setTimeout(() => {
// 
//         setSendStatus('idle');
// 
//         setSuccessMessage(null);
// 
//         if (onSaved) onSaved();
// 
//       }, 2000);
// 
// 
// 
//     } catch (err) {
// 
//       setSendStatus('failed');
// 
//       setSendError(getApiErrorMessage(err, 'Failed to launch campaign'));
// 
//     } finally {
// 
//       isSubmittingRef.current = false;
// 
//     }
// 
//   };
// 
// 
// 
//   if (loading) return (
// 
//     <div className="loading-state">
// 
//       <div className="premium-loader"></div>
// 
//       <p>Loading project resources...</p>
// 
//     </div>
// 
//   );
// 
// 
// 
//   return (
// 
//     <div className="create-campaign-page">
// 
//       <div className="premium-container">
// 
//         <div className="premium-card main-form">
// 
//           {successMessage && <div className="success-banner">{successMessage}</div>}
// 
// 
// 
//           <section className="form-section">
// 
//             <label className="premium-label">Campaign Identity</label>
// 
//             <input
// 
//               type="text"
// 
//               className="premium-input"
// 
//               placeholder="e.g. Summer Special Offer 2024"
// 
//               value={campaignName}
// 
//               onChange={e => setCampaignName(e.target.value)}
// 
//             />
// 
// 
// 
//             <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept={uploadType === 'excel' ? '.xlsx,.xls' : '.csv'} style={{ display: 'none' }} />
// 
// 
// 
//             {/* Mobile Inline Bulk Panel */}
// 
//             {showBulkPanel && (
// 
//               <div className="mobile-only-bulk-panel">
// 
//                 <div className="bulk-panel-right">
// 
//                   <div className="bulk-panel-tabs">
// 
//                     {['excel', 'csv', 'paste'].map(tab => (
// 
//                       <button
// 
//                         key={tab}
// 
//                         className={`bulk-tab-btn ${bulkPanelTab === tab ? 'active' : ''}`}
// 
//                         onClick={() => setBulkPanelTab(tab)}
// 
//                       >
// 
//                         {tab === 'excel' ? '📊 Excel' : tab === 'csv' ? '📄 CSV' : '📋 Paste'}
// 
//                       </button>
// 
//                     ))}
// 
//                   </div>
// 
//                   {bulkPanelTab === 'excel' && (
// 
//                     <div className="bulk-panel-content">
// 
//                       <p className="panel-hint">Upload an Excel file (.xlsx or .xls) with phone numbers.</p>
// 
//                       <button className="upload-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setUploadType('excel'); fileInputRef.current.click(); }}>
// 
//                         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
// 
//                         Choose Excel File
// 
//                       </button>
// 
//                     </div>
// 
//                   )}
// 
//                   {bulkPanelTab === 'csv' && (
// 
//                     <div className="bulk-panel-content">
// 
//                       <p className="panel-hint">Upload a CSV file with phone numbers.</p>
// 
//                       <button className="upload-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setUploadType('csv'); fileInputRef.current.click(); }}>
// 
//                         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
// 
//                         Choose CSV File
// 
//                       </button>
// 
//                     </div>
// 
//                   )}
// 
//                   {bulkPanelTab === 'paste' && (
// 
//                     <div className="bulk-panel-content paste-view">
// 
//                       <p className="panel-hint">Paste phone numbers (one per line, with country code).</p>
// 
//                       <textarea className="paste-area" placeholder="e.g.&#10;919876543210&#10;919876543211" value={pastedText} onChange={e => setPastedText(e.target.value)} />
// 
//                       <div className="paste-actions">
// 
//                         <span className="live-count">{pastedText.split('\n').filter(l => l.trim()).length} numbers</span>
// 
//                         <button className="validate-btn" onClick={handleValidatePaste}>Validate</button>
// 
//                       </div>
// 
//                       {validationResult && (validationResult.valid.length > 0 || validationResult.invalid.length > 0) && (
// 
//                         <div className="validation-results">
// 
//                           <div className="res-item valid">✓ {validationResult.valid.length} Valid</div>
// 
//                           {validationResult.invalid.length > 0 && <div className="res-item invalid">✗ {validationResult.invalid.length} Invalid</div>}
// 
//                           <button className="premium-btn-primary add-pasted-btn" onClick={() => { handleAddPasted(); setShowBulkPanel(false); }}>Add {validationResult.valid.length} Contacts</button>
// 
//                         </div>
// 
//                       )}
// 
//                     </div>
// 
//                   )}
// 
//                 </div>
// 
//               </div>
// 
//             )}
// 
//           </section>
// 
// 
// 
//           <section className="form-section templates-selection">
// 
//             <div className="section-header">
// 
//               <label className="premium-label">Select Template</label>
// 
//             </div>
// 
// 
// 
//             {showDraftBanner && (
// 
//               <div className="draft-banner">
// 
//                 <span>You have an unsaved template draft.</span>
// 
//                 <button onClick={handleOpenTemplateModal}>Continue Draft →</button>
// 
//               </div>
// 
//             )}
// 
// 
// 
//             <div className="template-scroll-container">
// 
//               <div className="template-grid">
// 
//                 <div className="template-card create-card" onClick={handleOpenTemplateModal}>
// 
//                   <div className="plus-icon">+</div>
// 
//                   <span>Create Template</span>
// 
//                 </div>
// 
//                 {templates.map(t => (
// 
//                   <div
// 
//                     key={t.id}
// 
//                     className={`template-card ${selectedTemplateId === t.id.toString() ? 'selected' : ''} ${t.status === 'APPROVED' ? '' : 'disabled'}`}
// 
//                     onClick={() => t.status === 'APPROVED' && setSelectedTemplateId(t.id.toString())}
// 
//                   >
// 
//                     <div className="card-top">
// 
//                       <span className={`status-badge ${t.status.toLowerCase()}`}>{t.status}</span>
// 
//                       <span className="cat-tag">{t.category}</span>
// 
//                     </div>
// 
//                     <div className="card-name">{t.name}</div>
// 
//                     <div className="card-preview-text">{t.body.substring(0, 60)}...</div>
// 
//                   </div>
// 
//                 ))}
// 
//               </div>
// 
//             </div>
// 
//           </section>
// 
// 
// 
//           <section className="form-section contacts-selection">
// 
//             <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
// 
//               <label className="premium-label" style={{ margin: 0 }}>Select Recipients</label>
// 
//               <div className="selection-stats" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
// 
//                 <button
// 
//                   type="button"
// 
//                   className={`upload-btn bulk-upload-toggle ${showBulkPanel ? 'active' : ''}`}
// 
//                   onClick={() => setShowBulkPanel(v => !v)}
// 
//                   style={{
// 
//                     padding: '6px 12px',
// 
//                     height: '32px',
// 
//                     fontSize: '12px',
// 
//                     margin: 0,
// 
//                     borderRadius: '8px',
// 
//                     display: 'flex',
// 
//                     alignItems: 'center',
// 
//                     gap: '6px',
// 
//                     border: '1.5px solid transparent',
// 
//                     borderColor: showBulkPanel ? 'var(--brand-purple)' : 'var(--border-color)',
// 
//                     background: showBulkPanel ? 'var(--brand-purple)' : 'var(--surface-soft-2)',
// 
//                     color: showBulkPanel ? '#ffffff' : 'var(--brand-purple)',
// 
//                     fontWeight: '700',
// 
//                     cursor: 'pointer',
// 
//                     transition: 'all 0.2s',
// 
//                     boxShadow: 'none'
// 
//                   }}
// 
//                 >
// 
//                   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
// 
//                     <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
// 
//                     <polyline points="17 8 12 3 7 8"></polyline>
// 
//                     <line x1="12" y1="3" x2="12" y2="15"></line>
// 
//                   </svg>
// 
//                   Bulk Upload
// 
//                 </button>
// 
//                 <div style={{ width: '1px', height: '16px', background: 'var(--border-color)' }}></div>
// 
//                 <span>{selectedContactIds.length} Selected</span>
// 
//                 <button type="button" onClick={handleSelectAll} className="link-btn">
// 
//                   {selectedContactIds.length === filteredContacts.length && filteredContacts.length > 0 ? 'Deselect All' : 'Select All'}
// 
//                 </button>
// 
//               </div>
// 
//             </div>
// 
// 
// 
//             <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
// 
//               <div 
// 
//                 style={{ 
// 
//                   flex: 1, 
// 
//                   background: 'var(--bg-main)', 
// 
//                   border: '1.5px solid transparent', 
// 
//                   borderRadius: '10px', 
// 
//                   padding: '0 12px', 
// 
//                   display: 'flex', 
// 
//                   alignItems: 'center', 
// 
//                   height: '38px',
// 
//                   transition: 'all 0.2s ease',
// 
//                   overflow: 'hidden'
// 
//                 }}
// 
//                 onFocus={(e) => { e.currentTarget.style.border = '1.5px solid var(--brand-purple)'; e.currentTarget.style.background = 'var(--surface-white)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 77, 255, 0.08)'; }}
// 
//                 onBlur={(e) => { e.currentTarget.style.border = '1.5px solid transparent'; e.currentTarget.style.background = 'var(--bg-main)'; e.currentTarget.style.boxShadow = 'none'; }}
// 
//               >
// 
//                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-gray)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
// 
//                 <input 
// 
//                   type="text" 
// 
//                   placeholder="Search by phone number..." 
// 
//                   value={searchTerm} 
// 
//                   onChange={e => {
// 
//                     const val = e.target.value.replace(/\D/g, '');
// 
//                     if (val.length <= 10) setSearchTerm(val);
// 
//                   }} 
// 
//                   style={{ width: '100%', height: '100%', border: 'none', outline: 'none', background: 'transparent', marginLeft: '8px', fontSize: '13px', color: 'var(--text-dark)', fontWeight: '600' }}
// 
//                 />
// 
//               </div>
// 
// 
// 
//               <button 
// 
//                 type="button"
// 
//                 onClick={() => setShowAddModal(true)}
// 
//                 style={{
// 
//                   background: 'var(--brand-purple)',
// 
//                   color: 'white',
// 
//                   border: 'none',
// 
//                   borderRadius: '10px',
// 
//                   height: '38px',
// 
//                   padding: '0 14px',
// 
//                   fontSize: '13px',
// 
//                   fontWeight: '700',
// 
//                   cursor: 'pointer',
// 
//                   boxShadow: '0 4px 12px rgba(124, 77, 255, 0.2)',
// 
//                   display: 'flex',
// 
//                   alignItems: 'center',
// 
//                   gap: '6px',
// 
//                   whiteSpace: 'nowrap',
// 
//                   transition: 'all 0.2s'
// 
//                 }}
// 
//                 onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(124, 77, 255, 0.3)'; }}
// 
//                 onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 77, 255, 0.2)'; }}
// 
//               >
// 
//                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
// 
//                 Add Contact
// 
//               </button>
// 
//             </div>
// 
// 
// 
//             <div className="contacts-table-grid">
// 
//               {filteredContacts.length === 0 ? (
// 
//                 <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-gray)', fontSize: '13px', padding: '24px' }}>
// 
//                   No contacts found.
// 
//                 </div>
// 
//               ) : (
// 
//                 <>
// 
//                   <div className="contacts-column">
// 
//                     <div className="contacts-column-header">
// 
//                       <div className="checkbox-header-spacer" />
// 
//                       <span className="header-label name-lbl">Name</span>
// 
//                       <span className="header-label phone-lbl">Number</span>
// 
//                     </div>
// 
//                     {leftContacts.map(contact => {
// 
//                       const isSelected = selectedContactIds.includes(contact.id);
// 
//                       const cleanPhone = contact.phone_number?.replace(/\D/g, '') || '';
// 
//                       const displayPhone = cleanPhone.slice(-10);
// 
//                       const cleanName = contact.name?.replace(/\D/g, '') || '';
// 
//                       const displayName = contact.name && cleanName !== displayPhone ? contact.name : '—';
// 
//                       return (
// 
//                         <div
// 
//                           key={contact.id}
// 
//                           className={`contact-table-row ${isSelected ? 'selected' : ''}`}
// 
//                           onClick={() => handleToggleContact(contact.id)}
// 
//                         >
// 
//                           <div className="contact-grid-checkbox" style={{
// 
//                             borderColor: isSelected ? '#6366f1' : 'var(--border-color)',
// 
//                             background: isSelected ? '#6366f1' : 'transparent',
// 
//                             color: isSelected ? '#fff' : 'transparent',
// 
//                             marginRight: '8px'
// 
//                           }}>
// 
//                             {isSelected && <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>}
// 
//                           </div>
// 
//                           <span className="contact-name-cell" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
// 
//                             {displayName}
// 
//                           </span>
// 
//                           <span className="contact-phone-cell" style={{ fontFamily: 'monospace', marginLeft: 'auto', flexShrink: 0 }}>
// 
//                             {displayPhone}
// 
//                           </span>
// 
//                         </div>
// 
//                       );
// 
//                     })}
// 
//                   </div>
// 
//                   <div className="contacts-column">
// 
//                     <div className="contacts-column-header">
// 
//                       <div className="checkbox-header-spacer" />
// 
//                       <span className="header-label name-lbl">Name</span>
// 
//                       <span className="header-label phone-lbl">Number</span>
// 
//                     </div>
// 
//                     {rightContacts.map(contact => {
// 
//                       const isSelected = selectedContactIds.includes(contact.id);
// 
//                       const cleanPhone = contact.phone_number?.replace(/\D/g, '') || '';
// 
//                       const displayPhone = cleanPhone.slice(-10);
// 
//                       const cleanName = contact.name?.replace(/\D/g, '') || '';
// 
//                       const displayName = contact.name && cleanName !== displayPhone ? contact.name : '—';
// 
//                       return (
// 
//                         <div
// 
//                           key={contact.id}
// 
//                           className={`contact-table-row ${isSelected ? 'selected' : ''}`}
// 
//                           onClick={() => handleToggleContact(contact.id)}
// 
//                         >
// 
//                           <div className="contact-grid-checkbox" style={{
// 
//                             borderColor: isSelected ? '#6366f1' : 'var(--border-color)',
// 
//                             background: isSelected ? '#6366f1' : 'transparent',
// 
//                             color: isSelected ? '#fff' : 'transparent',
// 
//                             marginRight: '8px'
// 
//                           }}>
// 
//                             {isSelected && <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>}
// 
//                           </div>
// 
//                           <span className="contact-name-cell" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
// 
//                             {displayName}
// 
//                           </span>
// 
//                           <span className="contact-phone-cell" style={{ fontFamily: 'monospace', marginLeft: 'auto', flexShrink: 0 }}>
// 
//                             {displayPhone}
// 
//                           </span>
// 
//                         </div>
// 
//                       );
// 
//                     })}
// 
//                   </div>
// 
//                 </>
// 
//               )}
// 
//             </div>
// 
//           </section>
// 
//         </div>
// 
// 
// 
//         {/* Right Panel */}
// 
//         <div className="premium-card side-panel">
// 
//           {showBulkPanel ? (
// 
//             <div className="bulk-panel-right">
// 
//               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
// 
//                 <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-dark)' }}>Bulk Upload</h3>
// 
//                 <button
// 
//                   onClick={() => setShowBulkPanel(false)}
// 
//                   style={{ width: '28px', height: '28px', borderRadius: '50%', border: 'none', background: 'var(--surface-soft-2)', color: 'var(--text-gray)', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
// 
//                   onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#ef4444'; }}
// 
//                   onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-soft-2)'; e.currentTarget.style.color = 'var(--text-gray)'; }}
// 
//                 >✕</button>
// 
//               </div>
// 
//               <div className="bulk-panel-tabs">
// 
//                 {['excel', 'csv', 'paste'].map(tab => (
// 
//                   <button
// 
//                     key={tab}
// 
//                     className={`bulk-tab-btn ${bulkPanelTab === tab ? 'active' : ''}`}
// 
//                     onClick={() => setBulkPanelTab(tab)}
// 
//                   >
// 
//                     {tab === 'excel' ? '📊 Excel' : tab === 'csv' ? '📄 CSV' : '📋 Paste'}
// 
//                   </button>
// 
//                 ))}
// 
//               </div>
// 
//               {bulkPanelTab === 'excel' && (
// 
//                 <div className="bulk-panel-content">
// 
//                   <p className="panel-hint">Upload an Excel file (.xlsx or .xls) with phone numbers.</p>
// 
//                   <button className="upload-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setUploadType('excel'); fileInputRef.current.click(); }}>
// 
//                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
// 
//                     Choose Excel File
// 
//                   </button>
// 
//                 </div>
// 
//               )}
// 
//               {bulkPanelTab === 'csv' && (
// 
//                 <div className="bulk-panel-content">
// 
//                   <p className="panel-hint">Upload a CSV file with phone numbers column.</p>
// 
//                   <button className="upload-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setUploadType('csv'); fileInputRef.current.click(); }}>
// 
//                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
// 
//                     Choose CSV File
// 
//                   </button>
// 
//                 </div>
// 
//               )}
// 
//               {bulkPanelTab === 'paste' && (
// 
//                 <div className="bulk-panel-content">
// 
//                   <p className="panel-hint">Enter 10-digit numbers, separated by commas or lines.</p>
// 
//                   <textarea
// 
//                     className="paste-area"
// 
//                     placeholder={"Eg:- 9876543211,9876543210"}
// 
//                     value={pastedText}
// 
//                     onChange={e => setPastedText(e.target.value)}
// 
//                     style={{ height: '140px' }}
// 
//                   />
// 
//                   <div className="paste-actions">
// 
//                     <div className="live-count">{pastedText.split('\n').filter(l => l.trim()).length} numbers detected</div>
// 
//                     <button className="validate-btn" onClick={handleValidatePaste}>Validate</button>
// 
//                   </div>
// 
//                   {(validationResult.valid.length > 0 || validationResult.invalid.length > 0) && (
// 
//                     <div className="validation-results">
// 
//                       <div className="res-item valid">✓ Valid: {validationResult.valid.length} numbers</div>
// 
//                       <div className="res-item invalid">✗ Invalid: {validationResult.invalid.length} numbers</div>
// 
//                       {validationResult.valid.length > 0 && (
// 
//                         <button className="premium-btn-primary add-pasted-btn" onClick={() => { handleAddPasted(); setShowBulkPanel(false); }}>
// 
//                           Add {validationResult.valid.length} numbers →
// 
//                         </button>
// 
//                       )}
// 
//                     </div>
// 
//                   )}
// 
//                 </div>
// 
//               )}
// 
//             </div>
// 
//           ) : (
// 
//             <div className="summary-view">
// 
//               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
// 
//                 <h3 style={{ margin: 0 }}>Campaign Summary</h3>
// 
//                  <button
// 
//                   className="action-btn schedule-btn-premium"
// 
//                   style={{
// 
//                     display: 'flex', alignItems: 'center', gap: '4px',
// 
//                     background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
// 
//                     color: '#fff', border: 'none', padding: '5px 10px',
// 
//                     borderRadius: '8px', cursor: 'pointer', fontWeight: '700',
// 
//                     fontSize: '11px', boxShadow: '0 2px 6px rgba(99,102,241,0.15)',
// 
//                     transition: 'all 0.2s', height: '28px'
// 
//                   }}
// 
//                   disabled={sending}
// 
//                   onClick={() => {
// 
//                     if (!campaignName.trim()) {
// 
//                       showError('Please enter a campaign name');
// 
//                       return;
// 
//                     }
// 
//                     if (!selectedTemplateId) {
// 
//                       showError('Please select a template');
// 
//                       return;
// 
//                     }
// 
//                     if (selectedContactIds.length === 0) {
// 
//                       showError('Please select at least one contact');
// 
//                       return;
// 
//                     }
// 
//                     setShowScheduleModal(true);
// 
//                   }}
// 
//                 >
// 
//                   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
// 
//                   Schedule
// 
//                 </button>
// 
//               </div>
// 
//               <div className="summary-list">
// 
//                 <div className="summary-item">
// 
//                   <span className="label">Name</span>
// 
//                   <span className="value">{campaignName || 'Untitled Campaign'}</span>
// 
//                 </div>
// 
//                 <div className="summary-item">
// 
//                   <span className="label">Total Contacts</span>
// 
//                   <span className="value">{selectedContactIds.length}</span>
// 
//                 </div>
// 
//               </div>
// 
// 
// 
//               {selectedTemplate && (
// 
//                 <div className="template-preview-panel">
// 
//                   <label className="premium-label">Selected Template</label>
// 
//                   <div className="preview-card-compact" style={{ maxHeight: '400px', overflowY: 'auto' }}>
// 
//                     <div className="preview-header">
// 
//                       <span className="name">{selectedTemplate.name}</span>
// 
//                       <span className="badge">APPROVED</span>
// 
//                     </div>
// 
//                     {/* Compact Image Preview */}
// 
//                     {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedTemplate.header_type) && (
// 
//                       <div className="compact-media-preview" style={{ marginBottom: '12px' }}>
// 
//                         {headerMediaUrl ? (
// 
//                           <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
// 
//                             {selectedTemplate.header_type === 'IMAGE' && (
// 
//                               (headerMediaUrl.startsWith('http') || headerMediaUrl.startsWith('data:') || headerMediaUrl.startsWith('blob:')) ? (
// 
//                                 <img src={headerMediaUrl} alt="Preview" style={{ width: '100%', maxHeight: '140px', objectFit: 'cover', display: 'block' }} />
// 
//                               ) : (
// 
//                                 <div className="meta-handle-display-box" style={{ padding: '16px', background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', borderRadius: '8px', border: '1px solid rgba(117, 81, 255, 0.2)', width: '100%', boxSizing: 'border-box' }}>
// 
//                                   <span style={{ fontSize: '24px' }}>🖼️</span>
// 
//                                   <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--brand-purple)' }}>Meta Sync Media Configured</span>
// 
//                                   <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>ID: {headerMediaUrl}</span>
// 
//                                 </div>
// 
//                               )
// 
//                             )}
// 
//                             {selectedTemplate.header_type === 'VIDEO' && (
// 
//                               (headerMediaUrl.startsWith('http') || headerMediaUrl.startsWith('data:') || headerMediaUrl.startsWith('blob:')) ? (
// 
//                                 <video src={headerMediaUrl} style={{ width: '100%', maxHeight: '140px', objectFit: 'cover', display: 'block' }} controls />
// 
//                               ) : (
// 
//                                 <div className="meta-handle-display-box" style={{ padding: '16px', background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', borderRadius: '8px', border: '1px solid rgba(117, 81, 255, 0.2)', width: '100%', boxSizing: 'border-box' }}>
// 
//                                   <span style={{ fontSize: '24px' }}>🎥</span>
// 
//                                   <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--brand-purple)' }}>Meta Sync Video Configured</span>
// 
//                                   <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>ID: {headerMediaUrl}</span>
// 
//                                 </div>
// 
//                               )
// 
//                             )}
// 
//                             {selectedTemplate.header_type === 'DOCUMENT' && (
// 
//                               <div style={{ padding: '16px', background: 'var(--surface-soft)', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid var(--border-color)', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}>
// 
//                                 📄 <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-dark)', wordBreak: 'break-all' }}>
// 
//                                   {headerMediaFile?.name || (headerMediaUrl.startsWith('http') ? 'Document Attached' : `Meta Sync Document: ${headerMediaUrl}`)}
// 
//                                 </span>
// 
//                               </div>
// 
//                             )}
// 
//                             <button
// 
//                               onClick={() => { setHeaderMediaUrl(''); setHeaderMediaFile(null); }}
// 
//                               style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
// 
//                             >✕</button>
// 
//                           </div>
// 
//                         ) : (
// 
//                           <div style={{ padding: '16px', border: '1.5px dashed var(--border-color)', borderRadius: '8px', textAlign: 'center', background: 'var(--surface-soft)' }}>
// 
//                             <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-gray)', marginBottom: '8px' }}>
// 
//                               Header {selectedTemplate.header_type === 'IMAGE' ? 'Image' : selectedTemplate.header_type === 'VIDEO' ? 'Video' : 'Document'} Required
// 
//                             </div>
// 
//                             <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
// 
//                               <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>Provide media URL:</span>
// 
//                               <input 
// 
//                                 type="text"
// 
//                                 placeholder="Paste Media URL"
// 
//                                 style={{ 
// 
//                                   padding: '4px 8px', 
// 
//                                   borderRadius: '6px', 
// 
//                                   border: '1px solid #cbd5e1', 
// 
//                                   fontSize: '11px',
// 
//                                   outline: 'none',
// 
//                                   width: '150px'
// 
//                                 }}
// 
//                                 onChange={(e) => {
// 
//                                   setHeaderMediaUrl(e.target.value);
// 
//                                   setHeaderMediaFile(null);
// 
//                                 }}
// 
//                               />
// 
//                             </div>
// 
//                           </div>
// 
//                         )}
// 
//                       </div>
// 
//                     )}
// 
//                     <div className="preview-body message-body" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{formatWhatsAppText(getRenderedPreview(selectedTemplate.body, templateVariables))}</div>
// 
//                     {/* Render Template Buttons if they exist */}
// 
//                     {selectedTemplate.buttons && selectedTemplate.buttons.length > 0 && (
// 
//                       <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
// 
//                         {selectedTemplate.buttons.map((btn, idx) => (
// 
//                           <div key={idx} style={{ 
// 
//                             padding: '8px 12px', 
// 
//                             background: '#f1f5f9', 
// 
//                             border: '1px solid var(--border-color)', 
// 
//                             borderRadius: '8px',
// 
//                             textAlign: 'center',
// 
//                             fontSize: '13px',
// 
//                             fontWeight: '600',
// 
//                             color: '#3b82f6',
// 
//                             display: 'flex',
// 
//                             alignItems: 'center',
// 
//                             justifyContent: 'center',
// 
//                             gap: '6px'
// 
//                           }}>
// 
//                             {btn.type === 'URL' ? (
// 
//                               <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
// 
//                             ) : btn.type === 'PHONE_NUMBER' ? (
// 
//                               <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
// 
//                             ) : (
// 
//                               <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 10 4 15 9 20"></polyline><path d="M20 4v7a4 4 0 0 1-4 4H4"></path></svg>
// 
//                             )}
// 
//                             {btn.text}
// 
//                           </div>
// 
//                         ))}
// 
//                       </div>
// 
//                     )}
// 
//                   </div>
// 
// 
// 
//                   {/* Dynamic Variables Section */}
// 
//                   {Object.keys(templateVariables).filter(k => !isNaN(k)).length > 0 && (
// 
//                     <div className="variables-section" style={{ marginTop: '16px' }}>
// 
//                       <label className="var-section-label">PERSONALIZE MESSAGE</label>
// 
//                       <div className="var-inputs">
// 
//                         {Object.keys(templateVariables)
// 
//                           .filter(k => !isNaN(k))
// 
//                           .sort((a, b) => parseInt(a) - parseInt(b))
// 
//                           .map(v => (
// 
//                             <div key={v} className="var-field">
// 
//                               <span className="var-pill">{"{{"}{v}{"}}"}</span>
// 
//                               <input
// 
//                                 type="text"
// 
//                                 className="premium-input var-input"
// 
//                                 placeholder={`Value for var ${v}...`}
// 
//                                 value={templateVariables[v]}
// 
//                                 onChange={(e) => setTemplateVariables(prev => ({ ...prev, [v]: e.target.value }))}
// 
//                               />
// 
//                             </div>
// 
//                           ))}
// 
//                       </div>
// 
//                     </div>
// 
//                   )}
// 
//                 </div>
// 
//               )}
// 
// 
// 
//               <div className="campaign-actions-container" style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
// 
//                 <button
// 
//                   className="premium-btn-secondary"
// 
//                   style={{
// 
//                     width: '100%',
// 
//                     padding: '16px',
// 
//                     margin: 0,
// 
//                     fontWeight: '700',
// 
//                     cursor: sending ? 'not-allowed' : 'pointer'
// 
//                   }}
// 
//                   disabled={sending || !campaignName.trim()}
// 
//                   onClick={handleSaveDraft}
// 
//                 >
// 
//                   {sending ? 'Saving...' : 'Save Draft'}
// 
//                 </button>
// 
// 
// 
//                 <button
// 
//                   className={`premium-btn-primary send-btn ${sendStatus === 'sending' ? 'loading' : ''}`}
// 
//                   style={{
// 
//                     width: '100%',
// 
//                     padding: '16px',
// 
//                     margin: 0,
// 
//                     opacity: sendStatus === 'sending' || !campaignName || selectedContactIds.length === 0 || !selectedTemplateId ? 0.6 : 1,
// 
//                     cursor: sendStatus === 'sending' || !campaignName || selectedContactIds.length === 0 || !selectedTemplateId ? 'not-allowed' : 'pointer'
// 
//                   }}
// 
//                   disabled={sendStatus === 'sending' || !campaignName || selectedContactIds.length === 0 || !selectedTemplateId}
// 
//                   onClick={handleSendCampaign}
// 
//                 >
// 
//                   {sendStatus === 'sending' ? (
// 
//                     <>
// 
//                       <span style={{ display: 'inline-block', marginRight: '8px' }}>⏳</span>
// 
//                       Sending to {selectedContactIds.length} contacts...
// 
//                     </>
// 
//                   ) : sendStatus === 'sent' ? (
// 
//                     <>
// 
//                       <span style={{ display: 'inline-block', marginRight: '8px' }}>✓</span>
// 
//                       Campaign Sent Successfully!
// 
//                     </>
// 
//                   ) : (
// 
//                     editCampaignDraftId
// 
//                       ? (originalStatus === 'SCHEDULED' ? `Save Scheduled Campaign →` : `Save Changes →`)
// 
//                       : (scheduleTime 
// 
//                           ? `Schedule for ${selectedContactIds.length} contacts →` 
// 
//                           : `Send to ${selectedContactIds.length} contacts →`)
// 
//                   )}
// 
//                 </button>
// 
// 
// 
//                 {/* Error Message Display */}
// 
//                 {sendError && (
// 
//                   <div style={{
// 
//                     marginTop: '12px',
// 
//                     padding: '12px 14px',
// 
//                     background: '#fee2e2',
// 
//                     border: '1px solid #fecaca',
// 
//                     borderRadius: '8px',
// 
//                     color: '#dc2626',
// 
//                     fontSize: '13px',
// 
//                     fontWeight: '500',
// 
//                     display: 'flex',
// 
//                     alignItems: 'center',
// 
//                     gap: '8px'
// 
//                   }}>
// 
//                     <span>✗</span>
// 
//                     <span>{sendError}</span>
// 
//                     <button
// 
//                       onClick={() => setSendError(null)}
// 
//                       style={{
// 
//                         marginLeft: 'auto',
// 
//                         background: 'none',
// 
//                         border: 'none',
// 
//                         color: '#dc2626',
// 
//                         cursor: 'pointer',
// 
//                         fontSize: '16px',
// 
//                         padding: 0
// 
//                       }}
// 
//                     >
// 
//                       ✕
// 
//                     </button>
// 
//                   </div>
// 
//                 )}
// 
// 
// 
//                 {/* Success Message Display */}
// 
//                 {successMessage && (
// 
//                   <div style={{
// 
//                     marginTop: '12px',
// 
//                     padding: '12px 14px',
// 
//                     background: '#dcfce7',
// 
//                     border: '1px solid #86efac',
// 
//                     borderRadius: '8px',
// 
//                     color: '#16a34a',
// 
//                     fontSize: '13px',
// 
//                     fontWeight: '500',
// 
//                     display: 'flex',
// 
//                     alignItems: 'center',
// 
//                     gap: '8px',
// 
//                     animation: 'slideDown 0.3s ease-out'
// 
//                   }}>
// 
//                     <span>✓</span>
// 
//                     <span>{successMessage}</span>
// 
//                   </div>
// 
//                 )}
// 
//               </div>
// 
//             </div>
// 
//           )}
// 
//         </div>
// 
//       </div>
// 
// 
// 
//       {/* Template Modal */}
// 
//       {showTemplateModal && createPortal(
// 
//         <div className="wa-module">
// 
//           <div className="wa-modal-overlay" onClick={handleCloseTemplateModal}>
// 
//             <div className="wa-modal-content premium-card" onClick={e => e.stopPropagation()}>
// 
//               <div className="wa-modal-header">
// 
//                 <h2>{localStorage.getItem('campaign_template_draft') ? 'Continue Draft' : 'Create New Template'}</h2>
// 
//                 {isDrafting && <span className="drafting-indicator">Auto-saving...</span>}
// 
//                 <button className="close-modal" onClick={handleCloseTemplateModal}>✕</button>
// 
//               </div>
// 
//               <form onSubmit={handleSubmitTemplate}>
// 
//                 <div className="wa-modal-body">
// 
//                   <div className="input-group">
// 
//                     <label className="premium-label">Template Name</label>
// 
//                     <input
// 
//                       type="text"
// 
//                       className="premium-input"
// 
//                       placeholder="e.g. welcome_offer"
// 
//                       value={templateData.name}
// 
//                       onChange={e => setTemplateData({ ...templateData, name: e.target.value })}
// 
//                     />
// 
//                   </div>
// 
//                   <div className="input-group">
// 
//                     <label className="premium-label">Category</label>
// 
//                     <select
// 
//                       className="premium-select"
// 
//                       value={templateData.category}
// 
//                       onChange={e => setTemplateData({ ...templateData, category: e.target.value })}
// 
//                     >
// 
//                       <option value="MARKETING">Marketing</option>
// 
//                       <option value="UTILITY">Utility</option>
// 
//                       <option value="AUTHENTICATION">Authentication</option>
// 
//                     </select>
// 
//                   </div>
// 
//                   <div className="input-group">
// 
//                     <label className="premium-label">Body Content</label>
// 
//                     <textarea
// 
//                       className="premium-textarea"
// 
//                       placeholder="Type your message here..."
// 
//                       value={templateData.body}
// 
//                       onChange={e => setTemplateData({ ...templateData, body: e.target.value })}
// 
//                     />
// 
//                     <span className="hint-text">Variables like {"{{1}}"}, {"{{2}}"} are supported.</span>
// 
//                   </div>
// 
//                 </div>
// 
//                 <div className="wa-modal-footer">
// 
//                   <button type="button" className="premium-btn-secondary" onClick={handleCloseTemplateModal}>Cancel</button>
// 
//                   <button type="submit" className="premium-btn-primary" disabled={templateLoading}>
// 
//                     {templateLoading ? 'Submitting...' : 'Submit to Meta for Review'}
// 
//                   </button>
// 
//                 </div>
// 
//               </form>
// 
//             </div>
// 
//           </div>
// 
//         </div>,
// 
//         document.body
// 
//       )}
// 
// 
// 
//       {/* Schedule Campaign Modal */}
// 
//       {showScheduleModal && createPortal(
//         <div className="wa-module">
//           <div style={{
// 
//           position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
// 
//           background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',
// 
//           display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
// 
//           animation: 'fadeIn 0.2s ease-out',
//         }}>
// 
//           <div style={{
// 
//             background: 'var(--surface-white)', padding: '30px', borderRadius: '24px',
// 
//             width: '100%', maxWidth: '460px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
// 
//             border: '1px solid var(--border-color)'
// 
//           }}>
// 
//             <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
// 
//               <div style={{ background: 'var(--brand-purple-light)', padding: '10px', borderRadius: '12px', display: 'flex' }}>
// 
//                 <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
// 
//               </div>
// 
//               <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: 'var(--text-dark)' }}>Schedule Campaign</h2>
// 
//             </div>
// 
// 
// 
//             <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: 'var(--text-gray)', lineHeight: '1.5' }}>
// 
//               Choose a future date and time to broadcast your messages automatically.
// 
//             </p>
// 
// 
// 
//             <div style={{ marginBottom: '24px' }}>
// 
//               <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: 'var(--text-gray)', marginBottom: '8px' }}>Select Date & Time</label>
// 
//               <input
// 
//                 type="datetime-local"
// 
//                 className="premium-input"
// 
//                 value={scheduleTime}
// 
//                 onChange={e => setScheduleTime(e.target.value)}
// 
//                 min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
// 
//                 style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '2px solid var(--border-color)', background: 'var(--surface-white)', fontSize: '14px', outline: 'none' }}
// 
//               />
// 
//             </div>
// 
// 
// 
//             <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
// 
//               <button
// 
//                 style={{
// 
//                   background: 'var(--surface-soft)', color: 'var(--text-gray)', border: 'none',
// 
//                   padding: '12px 20px', borderRadius: '12px', fontWeight: '700',
// 
//                   fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s'
// 
//                 }}
// 
//                 onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border-color)'}
// 
//                 onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface-soft)'}
// 
//                 onClick={() => {
// 
//                   setShowScheduleModal(false);
// 
//                   setScheduleTime('');
// 
//                 }}
// 
//                 disabled={sending}
// 
//               >
// 
//                 Cancel
// 
//               </button>
// 
//               <button
// 
//                 style={{
// 
//                   background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
// 
//                   color: '#ffffff', border: 'none', padding: '12px 24px',
// 
//                   borderRadius: '12px', fontWeight: '700', fontSize: '14px',
// 
//                   cursor: 'pointer', transition: 'all 0.2s',
// 
//                   boxShadow: '0 4px 12px rgba(99,102,241,0.2)'
// 
//                 }}
// 
//                 onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 6px 16px rgba(99,102,241,0.3)'}
// 
//                 onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.2)'}
// 
//                 onClick={async () => {
// 
//                   await handleSendCampaign();
// 
//                   setShowScheduleModal(false);
// 
//                 }}
// 
//                 disabled={sending || !scheduleTime}
// 
//               >
// 
//                 {sending ? 'Scheduling...' : 'Confirm Schedule'}
// 
//               </button>
// 
//             </div>
//           </div>
//         </div>
//         </div>,
//         document.body
//       )}
// 
// 
// 
//       <AddContactModal 
// 
//         visible={showAddModal} 
// 
//         onClose={() => setShowAddModal(false)} 
// 
//         onSaved={async (createdContact) => {
// 
//           try {
// 
//             setLoading(true);
// 
//             const contactsData = await getContacts();
// 
//             const loadedContacts = Array.isArray(contactsData?.results) ? contactsData.results : (Array.isArray(contactsData) ? contactsData : []);
// 
//             setDbContacts(loadedContacts);
// 
//             if (createdContact?.id) {
// 
//               setSelectedContactIds(prev => [...prev, createdContact.id]);
// 
//             }
// 
//           } catch (err) {
// 
//             console.error("Failed to refresh contacts:", err);
// 
//           } finally {
// 
//             setLoading(false);
// 
//           }
// 
//         }} 
// 
//       />
// 
// 
// 
//       {closeAttempted && createPortal(
// 
//         <div className="wa-modal-overlay" style={{ zIndex: 11000 }}>
// 
//           <div className="modal-content premium-card" style={{ maxWidth: '400px', textAlign: 'center', padding: '24px' }}>
// 
//             {editCampaignDraftId ? (
// 
//               <>
// 
//                 <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '12px', color: 'var(--text-dark)' }}>Save Changes to Campaign?</h3>
// 
//                 <p style={{ fontSize: '14px', color: 'var(--text-gray)', marginBottom: '24px' }}>You have unsaved changes to this campaign.</p>
// 
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
// 
//                   <button 
// 
//                     className="premium-btn-primary" 
// 
//                     style={{ width: '100%' }} 
// 
//                     onClick={['SCHEDULED', 'RUNNING'].includes(originalStatus?.toUpperCase()) ? handleModalSaveScheduled : handleModalSaveDraft}
// 
//                   >
// 
//                     {['SCHEDULED', 'RUNNING'].includes(originalStatus?.toUpperCase()) ? 'Save Changes' : 'Save'}
// 
//                   </button>
// 
//                   <button 
// 
//                     className="premium-btn-secondary discard-btn" 
// 
//                     style={{ 
// 
//                       width: '100%', 
// 
//                       background: 'var(--surface-soft-2)', 
// 
//                       color: '#ef4444', 
// 
//                       border: '1.5px solid #fecaca',
// 
//                       fontWeight: '700'
// 
//                     }} 
// 
//                     onClick={handleModalDiscard}
// 
//                   >
// 
//                     Discard
// 
//                   </button>
// 
//                   <button className="premium-btn-secondary" style={{ width: '100%' }} onClick={onCancelClose}>Cancel</button>
// 
//                 </div>
// 
//               </>
// 
//             ) : (
// 
//               <>
// 
//                 <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '12px', color: 'var(--text-dark)' }}>Save Campaign as Draft?</h3>
// 
//                 <p style={{ fontSize: '14px', color: 'var(--text-gray)', marginBottom: '24px' }}>You have unsaved changes.</p>
// 
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
// 
//                   <button className="premium-btn-primary" style={{ width: '100%' }} onClick={handleModalSaveDraft}>Save Draft</button>
// 
//                   <button 
// 
//                     className="premium-btn-secondary discard-btn" 
// 
//                     style={{ 
// 
//                       width: '100%', 
// 
//                       background: 'var(--surface-soft-2)', 
// 
//                       color: '#ef4444', 
// 
//                       border: '1.5px solid #fecaca',
// 
//                       fontWeight: '700'
// 
//                     }} 
// 
//                     onClick={handleModalDiscard}
// 
//                   >
// 
//                     Discard
// 
//                   </button>
// 
//                   <button className="premium-btn-secondary" style={{ width: '100%' }} onClick={onCancelClose}>Cancel</button>
// 
//                 </div>
// 
//               </>
// 
//             )}
// 
//           </div>
// 
//         </div>,
// 
//         document.body
// 
//       )}
// 
// 
// 
//       <style dangerouslySetInnerHTML={{
// 
//         __html: `
// 
//         .create-campaign-page { 
// 
//           --text-gray: #000000;
// 
//           --text-muted: #000000;
// 
//           --text-dark: #000000;
// 
//           flex: 1; 
// 
//           display: flex; 
// 
//           flex-direction: column; 
// 
//           padding: 0; 
// 
//           animation: fadeIn 0.3s ease; 
// 
//           box-sizing: border-box; 
// 
//           min-height: 0; 
// 
//         }
// 
//         @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
// 
//         
// 
//         .premium-header { display: flex; align-items: center; gap: var(--layout-gap); margin-bottom: var(--layout-gap); }
// 
//         .premium-header h1 { font-size: 24px; font-weight: 800; color: var(--text-dark); margin: 0; }
// 
//         .premium-back-btn { width: 40px; height: 40px; border-radius: var(--radius-sm); border: 1px solid var(--border-color); background: var(--surface-white); cursor: pointer; display: flex; align-items: center; justify-content: center; color: #dc2626; transition: all 0.2s; }
// 
//         .premium-back-btn:hover { background: #fee2e2; color: #dc2626; border-color: #fecaca; transform: translateX(-2px); }
// 
// 
// 
//         .premium-container { display: grid; grid-template-columns: minmax(0, 1fr) 380px; gap: var(--layout-gap); flex: 1; min-height: 0; }
// 
//         @media (max-width: 1024px) {
// 
//           .premium-container { grid-template-columns: 1fr; }
// 
//           .premium-header { flex-direction: column; align-items: flex-start; gap: var(--layout-gap); }
// 
//           .side-panel .bulk-panel-right { display: none !important; }
// 
//           .mobile-only-bulk-panel { display: block; margin-top: var(--layout-gap); }
// 
//           .mobile-only-bulk-panel .bulk-panel-right { animation: fadeIn 0.2s ease; background: var(--surface-soft); border-radius: var(--radius-md); border: 1.5px solid var(--border-color); overflow: hidden; }
// 
//           .template-grid { overflow-x: auto; scroll-snap-type: x mandatory; padding-bottom: var(--layout-gap); }
// 
//           .template-card { scroll-snap-align: start; flex: 0 0 80%; }
// 
//         }
// 
//         @media (max-width: 768px) {
// 
//           .create-campaign-page { padding: 0 !important; }
// 
//         }
// 
//         @media (max-width: 480px) {
// 
//           .contacts-grid-container { grid-template-columns: 1fr; }
// 
//           .premium-card { padding: var(--container-padding); }
// 
//           .upload-btn { padding: 8px; font-size: 12px; }
// 
//           .premium-btn-primary { padding: 10px 16px; }
// 
//           .premium-btn-secondary { padding: 10px 16px; }
// 
//         }
// 
//         @media (min-width: 1025px) {
// 
//           .mobile-only-bulk-panel { display: none !important; }
// 
//         }
// 
//         .premium-card { background: var(--surface-white); border-radius: var(--container-radius); padding: var(--container-padding); box-shadow: var(--shadow-sm); border: 1px solid var(--border-color); overflow-y: auto; }
// 
//         
// 
//         .upload-buttons { display: flex; gap: var(--layout-gap); margin-top: var(--layout-gap); flex-wrap: wrap; }
// 
//         .upload-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 10px; border-radius: var(--radius-sm); border: 2px solid var(--surface-soft-2); background: var(--surface-white); color: #6366f1; font-weight: 700; font-size: 13px; cursor: pointer; transition: all 0.2s; }
// 
//         .upload-btn:hover, .upload-btn.active { background: #6366f1; color: #fff; border-color: #6366f1; transform: translateY(-2px); }
// 
//         .bulk-upload-toggle { flex: none; padding: 10px 16px; gap: 10px; }
// 
// 
// 
//         .bulk-panel { margin-top: var(--layout-gap); background: var(--surface-soft); border: 1.5px solid var(--border-color); border-radius: var(--radius-md); overflow: hidden; animation: fadeIn 0.2s ease; }
// 
//         .bulk-panel-tabs { display: flex; border-bottom: 1px solid var(--border-color); background: var(--surface-white); }
// 
//         .bulk-tab-btn { flex: 1; padding: 12px 8px; border: none; background: transparent; cursor: pointer; font-size: 12px; font-weight: 700; color: var(--text-gray); transition: all 0.2s; border-bottom: 2px solid transparent; margin-bottom: -1px; }
// 
//         .bulk-tab-btn.active { color: #6366f1; border-bottom-color: #6366f1; background: var(--brand-purple-light); }
// 
//         .bulk-panel-content { padding: var(--container-padding); display: flex; flex-direction: column; gap: var(--layout-gap); }
// 
//         .bulk-panel-right { animation: fadeIn 0.2s ease; }
// 
//         .bulk-panel-right .bulk-panel-tabs { border-radius: var(--radius-sm); overflow: hidden; border: 1.5px solid var(--border-color); margin-bottom: var(--layout-gap); }
// 
// 
// 
//         .premium-label { font-size: 11px; font-weight: 800; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; display: block; }
// 
//         .premium-input { width: 100%; padding: 14px 18px; border-radius: var(--radius-sm); border: 2px solid var(--border-color); background: var(--surface-soft); font-size: 15px; outline: none; transition: all 0.2s; }
// 
//         .premium-input:focus { border-color: #6366f1; background: var(--surface-white); box-shadow: 0 0 0 4px rgba(99,102,241,0.1); }
// 
// 
// 
//         .templates-selection { margin-top: var(--space-xl); width: 100%; max-width: 100%; overflow: hidden; }
// 
//         .template-scroll-container { position: relative; }
// 
//         .template-scroll-container::after {
// 
//           content: '→';
// 
//           position: absolute;
// 
//           right: 0;
// 
//           top: 0;
// 
//           height: 100%;
// 
//           width: 60px;
// 
//           background: linear-gradient(to right, transparent, var(--surface-white));
// 
//           display: flex;
// 
//           align-items: center;
// 
//           justify-content: flex-end;
// 
//           padding-right: 12px;
// 
//           color: #6366f1;
// 
//           font-size: 20px;
// 
//           font-weight: 900;
// 
//           pointer-events: none;
// 
//           opacity: 0.8;
// 
//         }
// 
// 
// 
//         .template-grid { 
// 
//           display: flex; 
// 
//           gap: 12px; 
// 
//           margin-top: 16px; 
// 
//           overflow-x: auto; 
// 
//           overflow-y: hidden; 
// 
//           padding: 8px 4px 16px 4px; 
// 
//           scrollbar-width: thin; 
// 
//           scrollbar-color: #6366f1 #f1f5f9; 
// 
//           -webkit-overflow-scrolling: touch; 
// 
//         }
// 
//         .template-grid::-webkit-scrollbar { height: 6px; }
// 
//         .template-grid::-webkit-scrollbar-track { background: var(--surface-soft-2); border-radius: 10px; }
// 
//         .template-grid::-webkit-scrollbar-thumb { background: #6366f1; border-radius: 10px; }
// 
//         
// 
//         .template-card { flex: 0 0 160px; height: 110px; background: var(--surface-white); border: 2px solid var(--border-color); border-radius: 14px; padding: 12px; cursor: pointer; transition: all 0.2s; position: relative; display: flex; flex-direction: column; }
// 
//         .template-card:hover:not(.disabled) { border-color: #6366f1; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
// 
//         .template-card.selected { border-color: #6366f1; background: var(--brand-purple-light); box-shadow: 0 4px 12px rgba(99,102,241,0.1); }
// 
//         .template-card.disabled { opacity: 0.6; cursor: not-allowed; background: var(--surface-soft); }
// 
//         
// 
//         .create-card { border-style: dashed; align-items: center; justify-content: center; color: #6366f1; font-weight: 700; gap: 4px; }
// 
//         .create-card .plus-icon { font-size: 24px; line-height: 1; }
// 
//         .create-card span { font-size: 11px; }
// 
//         
// 
//         .card-top { display: flex; justify-content: space-between; margin-bottom: 6px; }
// 
//         .status-badge { font-size: 7px; font-weight: 800; padding: 1px 4px; border-radius: 3px; text-transform: uppercase; }
// 
//         .status-badge.approved { background: #dcfce7; color: #166534; }
// 
//         .status-badge.pending { background: #fef9c3; color: #854d0e; }
// 
//         .status-badge.rejected { background: #fee2e2; color: #991b1b; }
// 
//         .cat-tag { font-size: 7px; color: var(--text-muted); font-weight: 700; }
// 
//         .card-name { font-size: 12px; font-weight: 800; color: var(--text-dark); margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
// 
//         .card-preview-text { font-size: 9px; color: var(--text-gray); line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
// 
// 
// 
//         .success-banner { background: #dcfce7; color: #166534; padding: 12px 20px; border-radius: 12px; font-weight: 700; margin-bottom: 20px; font-size: 14px; animation: slideDown 0.3s ease; }
// 
//         @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
// 
// 
// 
//         .draft-banner { background: var(--brand-purple-light); border: 1px solid var(--brand-purple-light); padding: 12px 20px; border-radius: 12px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; font-size: 13px; font-weight: 600; color: #4338ca; }
// 
//         .draft-banner button { background: none; border: none; color: #6366f1; font-weight: 800; cursor: pointer; padding: 0; margin-left: 12px; }
// 
// 
// 
//         .contacts-selection { margin-top: var(--space-xl); }
// 
//         .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
// 
//         .selection-stats { font-size: 13px; font-weight: 700; color: var(--text-gray); display: flex; gap: 12px; align-items: center; }
// 
//         .link-btn { background: var(--surface-soft-2); border: none; padding: 4px 12px; border-radius: 8px; color: #6366f1; font-weight: 700; cursor: pointer; font-size: 12px; }
// 
//         
// 
//         .search-bar { display: flex; align-items: center; gap: 12px; background: var(--surface-soft-2); padding: 12px 16px; border-radius: 14px; margin-bottom: 16px; }
// 
//         .search-bar input { background: none; border: none; outline: none; width: 100%; font-size: 14px; }
// 
// 
// 
//         .contacts-table-grid {
// 
//           display: flex;
// 
//           gap: 24px;
// 
//           height: 434px; 
// 
//           overflow-y: auto; 
// 
//           background: var(--surface-white); 
// 
//           border: 1px solid var(--border-color);
// 
//           border-radius: var(--radius-md);
// 
//           padding: 0;
// 
//           box-sizing: border-box;
// 
//         }
// 
//         .contacts-column {
// 
//           flex: 1;
// 
//           display: flex;
// 
//           flex-direction: column;
// 
//         }
// 
//         .contacts-column-header {
// 
//           display: flex;
// 
//           align-items: center;
// 
//           height: 34px;
// 
//           border-bottom: 1px solid var(--border-color);
// 
//           position: sticky;
// 
//           top: 0;
// 
//           background: var(--brand-purple-light); /* light and clean purple/zinc background */
// 
//           padding: 0 16px;
// 
//           z-index: 10;
// 
//           font-size: 11px;
// 
//           font-weight: 800;
// 
//           color: var(--text-muted);
// 
//           text-transform: uppercase;
// 
//           letter-spacing: 0.05em;
// 
//           box-sizing: border-box;
// 
//         }
// 
//         .checkbox-header-spacer {
// 
//           width: 18px;
// 
//           margin-right: 8px;
// 
//           flex-shrink: 0;
// 
//         }
// 
//         .header-label.name-lbl {
// 
//           flex: 1;
// 
//         }
// 
//         .header-label.phone-lbl {
// 
//           margin-left: auto;
// 
//           flex-shrink: 0;
// 
//         }
// 
//         .contact-table-row {
// 
//           display: flex;
// 
//           align-items: center;
// 
//           height: 40px;
// 
//           border-bottom: 1px solid var(--border-color);
// 
//           cursor: pointer;
// 
//           transition: all 0.15s ease;
// 
//           font-size: 13px;
// 
//           color: var(--text-dark);
// 
//           font-weight: 600;
// 
//           box-sizing: border-box;
// 
//           padding: 0 16px;
// 
//         }
// 
//         .contact-table-row:hover {
// 
//           color: #6366f1;
// 
//         }
// 
//         .contact-table-row.selected {
// 
//           color: var(--text-dark);
// 
//         }
// 
//         .contact-table-row .contact-grid-checkbox { 
// 
//           width: 18px; 
// 
//           height: 18px; 
// 
//           border-radius: 6px; 
// 
//           border: 2px solid var(--border-color); 
// 
//           display: flex; 
// 
//           align-items: center; 
// 
//           justify-content: center; 
// 
//           transition: all 0.15s;
// 
//           flex-shrink: 0;
// 
//         }
// 
//         .contact-table-row.selected .contact-grid-checkbox {
// 
//           border-color: #6366f1;
// 
//           background: #6366f1;
// 
//         }
// 
//         .contact-table-row:hover .contact-grid-checkbox {
// 
//           border-color: #6366f1;
// 
//         }
// 
//         .contact-name-cell {
// 
//           font-weight: 700;
// 
//         }
// 
// 
// 
//         .side-panel { position: sticky; top: 20px; }
// 
//         .summary-view h3 { margin: 0 0 20px 0; font-size: 18px; font-weight: 800; color: var(--text-dark); }
// 
//         .summary-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
// 
//         .summary-item { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: var(--surface-soft); border-radius: 12px; border: 1px solid var(--border-color); }
// 
//         .summary-item .label { font-size: 12px; color: var(--text-gray); font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em; }
// 
//         .summary-item .value { font-size: 14px; font-weight: 800; color: var(--text-dark); }
// 
//         
// 
//         .template-preview-panel { margin-top: 24px; border-top: 1px solid var(--border-color); padding-top: 24px; }
// 
//         .preview-card-compact { background: var(--surface-soft); border-radius: 18px 18px 18px 4px; padding: 16px; border: 1px solid var(--border-color); position: relative; margin-top: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
// 
//         .preview-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid rgba(99,102,241,0.1); }
// 
//         .preview-header .name { font-size: 13px; font-weight: 800; color: var(--text-dark); }
// 
//         .preview-header .badge { font-size: 9px; font-weight: 800; color: #fff; background: #10b981; padding: 2px 8px; border-radius: 20px; text-transform: uppercase; }
// 
//         .preview-body { font-size: 13px; color: var(--text-dark); line-height: 1.6; white-space: pre-wrap; font-family: inherit; }
// 
//         .send-btn { width: 100%; margin-top: 24px; padding: 16px; font-size: 15px; box-shadow: 0 10px 15px -3px rgba(99,102,241,0.3); }
// 
// 
// 
//         .variables-section { margin-top: 24px; animation: fadeIn 0.3s ease; }
// 
//         .var-section-label { font-size: 11px; font-weight: 800; color: var(--text-muted); letter-spacing: 0.05em; display: block; margin-bottom: 12px; }
// 
//         .var-inputs { display: flex; flex-direction: column; gap: 10px; }
// 
//         .var-field { display: flex; align-items: center; gap: 10px; background: var(--surface-white); padding: 6px; border-radius: 12px; border: 1.5px solid var(--border-color); transition: all 0.2s; }
// 
//         .var-field:focus-within { border-color: #6366f1; box-shadow: 0 4px 12px rgba(99,102,241,0.05); }
// 
//         .var-pill { background: var(--brand-purple-light); color: #6366f1; font-family: monospace; font-weight: 800; font-size: 12px; padding: 6px 10px; border-radius: 8px; min-width: 45px; text-align: center; }
// 
//         .var-input { border: none !important; background: transparent !important; padding: 8px !important; font-size: 13px !important; box-shadow: none !important; }
// 
// 
// 
//         .paste-view { display: flex; flex-direction: column; gap: 20px; }
// 
//         .panel-header { display: flex; justify-content: space-between; align-items: center; }
// 
//         .panel-header h3 { margin: 0; font-size: 18px; font-weight: 800; color: var(--text-dark); }
// 
//         .close-panel { width: 32px; height: 32px; border-radius: 50%; border: none; background: var(--surface-soft-2); color: var(--text-gray); font-size: 20px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
// 
//         .close-panel:hover { background: #fee2e2; color: #ef4444; }
// 
//         
// 
//         .panel-hint { font-size: 13px; color: var(--text-gray); line-height: 1.5; margin: 0; }
// 
//         .paste-area { width: 100%; height: 180px; padding: 16px; border-radius: 16px; border: 2px solid var(--border-color); background: var(--surface-soft); font-family: 'JetBrains Mono', 'Fira Code', monospace; font-size: 13px; resize: none; transition: all 0.3s; outline: none; }
// 
//         .paste-area:focus { border-color: #6366f1; background: var(--surface-white); box-shadow: 0 10px 20px rgba(99,102,241,0.08); }
// 
//         
// 
//         .paste-actions { display: flex; justify-content: space-between; align-items: center; }
// 
//         .live-count { font-size: 12px; font-weight: 700; color: #6366f1; background: var(--brand-purple-light); padding: 4px 12px; border-radius: 20px; }
// 
//         .validate-btn { background: var(--surface-white); border: 2px solid #6366f1; color: #6366f1; padding: 8px 20px; border-radius: 10px; font-weight: 800; font-size: 13px; cursor: pointer; transition: all 0.2s; }
// 
//         .validate-btn:hover { background: #6366f1; color: #fff; }
// 
// 
// 
//         .validation-results { background: var(--surface-soft); border-radius: 16px; padding: 16px; border: 1px solid var(--border-color); display: flex; flex-direction: column; gap: 12px; animation: slideIn 0.3s ease; }
// 
//         @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
// 
//         .res-item { font-size: 12px; font-weight: 700; }
// 
//         .res-item.valid { color: #10b981; }
// 
//         .res-item.invalid { color: #ef4444; }
// 
//         .add-pasted-btn { width: 100%; padding: 12px; font-size: 13px; margin-top: 4px; }
// 
// 
// 
//         .modal-overlay { 
// 
//           --text-gray: #000000;
// 
//           --text-muted: #000000;
// 
//           --text-dark: #000000;
// 
//           position: fixed; 
// 
//           top: 0; 
// 
//           left: 0; 
// 
//           right: 0; 
// 
//           bottom: 0; 
// 
//           background: rgba(0,0,0,0.4); 
// 
//           backdrop-filter: blur(4px); 
// 
//           display: flex; 
// 
//           align-items: center; 
// 
//           justify-content: center; 
// 
//           z-index: 1000; 
// 
//         }
// 
//         .modal-content { 
// 
//           --text-gray: #000000;
// 
//           --text-muted: #000000;
// 
//           --text-dark: #000000;
// 
//           width: 100%; 
// 
//           max-width: 500px; 
// 
//           animation: modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); 
// 
//         }
// 
//         @keyframes modalIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
// 
//         .modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
// 
//         .modal-header h2 { font-size: 20px; font-weight: 800; margin: 0; }
// 
//         .drafting-indicator { font-size: 10px; color: #6366f1; font-weight: 700; margin-left: 12px; font-style: italic; }
// 
//         .close-modal { background: none; border: none; font-size: 24px; color: #dc2626; cursor: pointer; transition: color 0.2s; }
// 
//         .close-modal:hover { color: #b91c1c; }
// 
//         .input-group { margin-bottom: 16px; }
// 
//         .template-body-area { height: 120px; resize: none; font-family: inherit; }
// 
//         .hint { font-size: 10px; color: var(--text-muted); margin-top: 4px; }
// 
//         .modal-footer { display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px; }
// 
// 
// 
//         .premium-btn-primary { background: #6366f1; color: #fff; border: none; padding: 12px 24px; border-radius: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
// 
//         .premium-btn-primary:hover:not(:disabled) { background: #4f46e5; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(99,102,241,0.3); }
// 
//         .premium-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
// 
//         .premium-btn-secondary { background: var(--surface-soft-2); color: var(--text-gray); border: none; padding: 12px 24px; border-radius: 12px; font-weight: 700; cursor: pointer; }
// 
// 
// 
//         .premium-loader { width: 30px; height: 30px; border: 3px solid var(--border-color); border-top-color: var(--brand-purple); border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 12px; }
// 
//         @keyframes spin { to { transform: rotate(360deg); } }
// 
//         .loading-state { height: 300px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--text-gray); font-weight: 600; }
// 
//       `}} />
// 
//     </div>
// 
//   );
// 
// };
// 
// 
// 
// export default CreateCampaign;
// 
// 
// 
// 
// 
// 
// 
// 
